import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpEvent, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { map, Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

export interface LearningResource {
  _id: string;
  title: string;
  description?: string;
  fileUrl: string;
  type: string;
  path?: string;
  moduleName?: string;
  markedComplete?: boolean;     // ← ADD THIS
  timeCredited?: boolean;       // ← ADD THIS
  totalPages?: number;          // optional: for PDFs
  durationMinutes?: number;     // optional: for videos/audio
}

export interface PaginationInfo {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  previousPage: number | null;
  nextPage: number | null;
  itemsPerPage: number;
}

export interface LearningResourcesResponse {
  status: number;
  pagination: PaginationInfo;
  data: LearningResource[];
}

export interface Comment {
  id: string;
  _id: string;
  postId: string;
  content: string;
  createdBy: { _id: string; firstName: string; lastName: string };
  isEdited: boolean;
  likes: string[];
  createdAt: string;
  updatedAt: string;
  hasReply?: boolean;
  __v?: number;
}

export interface Reply {
  id: string;
  _id: string;
  commentId: string;
  userId: { _id: string; firstName: string; lastName: string };
  content: string;
  isEdited: boolean;
  likes: string[];
  createdAt: string;
  updatedAt: string;
  __v?: number;
}
export interface WebinarResource {
  _id: string;
  webinarId: string;
  title: string;
  description: string;
  type: 'live' | 'pdf' | 'video';
  fileUrl: string;
  path: string;
  uploadedById: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebinarSession {
  date: string;
  startTime: string;
  endTime: string;
}

export interface WebinarDetail {
  _id: string;
  title: string;
  description: string;
  sessions: WebinarSession[];
  fee: number;
  status: string;
}

export interface WebinarBooking {
  _id: string;
  webinarId: string | WebinarDetail;
  studentId: string;
  amountPaid: number;
  status: string;
  bookedAt: string;
  createdAt: string;
  updatedAt: string;
}
export interface ChatSearchResponse {
  status: number;
  data: ChatMessage[];
  pagination?: PaginationInfo;
}

export interface ChatMessage {
  _id: string;
  content: string;
  sender: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
  };
  receiver?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  conversationId: string;
  messageType: 'text' | 'image' | 'file' | 'system';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Conversation {
  _id: string;
  participants: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
    role: string;
  }[];
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: string;
  };
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}
interface PaymentMethod {
  _id?: string;
  name: string;
  key: string;
  icon?: string;
  status: 'enabled' | 'disabled';
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}
export interface ConversationsResponse {
  status: number;
  data: Conversation[];
  pagination?: PaginationInfo;
}

export interface SingleMessageResponse {
  status: number;
  data: ChatMessage;
}

export interface ApiResponse {
  status: number;
  message: string;
  data?: any;
}

export interface AssignmentTopic {
  title: string;
  marks: number;
  sampleAnswer?: string;
}

export interface Assignment {
  _id?: string;
  title: string;
  description: string;
  image?: string;
  submitDate: string;
  totalMarks: number;
  passingMarks: number;
  topics: AssignmentTopic[];
}


export interface AppNotification {
  _id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  image?: string | null;
  link?: string | null;
  meta?: any;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsResponse {
  status: number;
  pagination: PaginationInfo;
  data: AppNotification[];
}

export interface YoutubeVideo {
  _id: string;
  title: string;
  description?: string;
  videoLink: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface YoutubeVideosResponse {
  status: number;
  pagination: PaginationInfo;
  videos: YoutubeVideo[];
}


export interface S3UploadedVideo {
  _id: string;
  title: string;
  description?: string;
  section: 'foundations' | 'advanced';
  fileUrl: string;
  s3Key: string;
  fileMimeType?: string | null;
  durationMinutes?: number | null;
  fileOriginalName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiPagination {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  previousPage: number | null;
  nextPage: number | null;
  itemsPerPage: number;
}

export interface S3VideosResponse {
  status: number;
  message: string;
  pagination: ApiPagination;
  data: S3UploadedVideo[];
}


@Injectable({
  providedIn: 'root',
})
export class CommonService {
  // baseUrl = 'https://incurred-even-amsterdam-judgment.trycloudflare.com/api';
  baseUrl = 'https://api.nexgencoaches.com/api';

  private get headers() {
    return {
      Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  constructor(private http: HttpClient) { }
  studentProfileImage$ = new BehaviorSubject<string>('');


  sendContactMessage(payload: any): Observable<any> {
    const url = `${this.baseUrl}/common/contact-us`;
    return this.http.post(url, payload);
  }

  // Common APIs section
  // Signup API
  signup(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/auth/signup`, payload);
  }

  // Verify Code API
  verifyCode(payload: { email: string; code: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/auth/verify-email`, payload);
  }

  // Resend Code API
  resendCode(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/auth/resend-code`, { email });
  }

  // Login API
  //   login(payload: {
  //   email: string;
  //   password: string;
  //   role: string;
  //   otpCode: string;
  // }): Observable<any> {
  //   return this.http.post(`${this.baseUrl}/common/auth/login`, payload);
  // }

  login(payload: any) {
    return this.http.post(`${this.baseUrl}/common/auth/login`, payload);

  }



  // Request Login OTP API
  requestLoginOtp(payload: {
    email: string;
    password: string;
    role: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/auth/request-login-otp`, payload);
  }

  // Forgot password
  forgotPassword(payload: { email: string }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/common/auth/forgot-password`,
      payload
    );
  }

  verifyResetCode(payload: { email: string; code: string }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/common/auth/verify-reset-code`,
      payload
    );
  }

  resetPassword(payload: {
    email: string;
    code: string;
    newPassword: string;
  }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/common/auth/reset-password`,
      payload
    );
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem('authToken');
  }

  getUserRole(): string | null {
    return localStorage.getItem('role');
  }

  // Logout API
  logout(): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/common/auth/logout`,
      {},
      { headers: this.headers }
    );
  }

  // Student profile
  updateStudentProfile(profileData: any, token: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/student/profile`, profileData, {
      headers: this.headers,
    });
  }

  // Get student profile
  getStudentProfile(token: string): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/student/profile`, { headers: this.headers })
      .pipe(
        map((res) => {
          if (res.student?.image) {
            res.student.image = `${res.student.image}`;
          }
          return res;
        })
      );
  }

  // Get student profile
  changePassword(payload: { currentPassword: string; newPassword: string; confirmPassword: string }, token: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/auth/change-password`, payload, { headers: this.headers });
  }

  // Get student stats
  getStudentStats(
    learningYear?: number,
    coachingYear?: number
  ): Observable<any> {
    const params: any = {};

    if (learningYear) {
      params.learningYear = learningYear;
    }
    if (coachingYear) {
      params.barterYear = coachingYear;
    }

    return this.http
      .get(`${this.baseUrl}/student/stats`, {
        headers: this.headers,
        params,
      })
      .pipe(
        map((res: any) => {
          if (res.data?.student?.image) {
            res.data.student.image = `${res.data.student.image}`;
          }
          return res;
        })
      );
  }

  getStudentManualTimeStats(
    page: number = 1,
    limit: number = 50,
    type: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('type', type)
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get(`${this.baseUrl}/student/stats/resources`, {
      headers: this.headers,
      params,
    });
  }

  /**
   * POST: Add manual time for student
   */
  addManualTime(data: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/stats/add-manual-time`,
      data,
      {
        headers: this.headers,
      }
    );
  }

  subtractManualTime(data: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/stats/subtract-manual-time`,
      data,
      {
        headers: this.headers,
      }
    );
  }


  // Quiz APIs
  getStudentQuizzes(
    type: 'attempted' | 'upcoming' | 'all' = 'attempted',
    limit = 20,
    page = 1
  ): Observable<any> {
    const url =
      type === 'all'
        ? `${this.baseUrl}/student/quiz?limit=${limit}&page=${page}`
        : `${this.baseUrl}/student/quiz?type=${type}&limit=${limit}&page=${page}`;
    return this.http.get(url, { headers: this.headers });
  }

  startQuiz(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/quiz/start`, payload, {
      headers: this.headers,
    });
  }

  submitQuiz(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/quiz/submit`, payload, {
      headers: this.headers,
    });
  }
  // for result detail
  viewSubmission(quizId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/quiz/view-submission/${quizId}`,
      {
        headers: this.headers,
      }
    );
  }

  searchQuizzes(title: string, limit = 20, page = 1): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/quiz/search?limit=${limit}&page=${page}&title=${title}`,
      { headers: this.headers }
    );
  }

  // Assignment APIs
  getAssignmentsForStudent(
    status: 'upcoming' | 'attempted' | 'overdue',
    limit = 20,
    page = 1
  ): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/assignment?status=${status}&limit=${limit}&page=${page}`,
      { headers: this.headers }
    );
  }

  getAssignmentDetails(assignmentId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/assignmentDetail/${assignmentId}`,
      { headers: this.headers }
    );
  }

  submitAssignment(assignmentId: string, payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/assignment/${assignmentId}`,
      payload,
      { headers: this.headers }
    );
  }

  getOwnSubmission(assignmentId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/submitted-assignment/${assignmentId}`,
      { headers: this.headers }
    );
  }

  // Learning resourses / Library api's
  getLearningResources(
    page = 1,
    limit = 50,
    sortBy: 'asc' | 'desc' = 'asc'
  ): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/common/learning-resources`,
      { params, headers: this.headers }
    );
  }

  // Learning resourses / Library api's
  getStudentLearningResources(
    page = 1,
    limit = 50,
    sortBy: 'asc' | 'desc' = 'asc'
  ): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/student/learning-resource`,
      { params, headers: this.headers }
    );
  }

  /**
   * Get all learning resources without pagination
   */
  getAllLearningResources(
    page = 1,
    limit = 50,
    sortBy: 'asc' | 'desc' = 'asc'): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/common/learning-resources`,
      { params, headers: this.headers }
    );
  }

  getAdminAllLearningResources(
    page = 1,
    limit = 50,
    sortBy: 'asc' | 'desc' = 'asc'): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/admin/learning-resource`,
      { params, headers: this.headers }
    );
  }



  //mark resource as complete
  markResourceComplete(resourceId: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/resources/mark-complete/${resourceId}`,
      {},                                           // ← empty body (or null)
      { headers: this.headers }                     // ← headers go here
    );
  }

  /**
   * Get learning resources by path filter
   * @param pathFilter - Filter by path (e.g., 'courses/web-dev', 'presentations/videos')
   */
  getLearningResourcesByPath(
    pathFilter: string,
    page = 1,
    limit = 100
  ): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('path', pathFilter);

    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/common/learning-resources`,
      { params, headers: this.headers }
    );
  }

  deleteLearningResourceModule(payload: { moduleName: string }): Observable<any> {
    return this.http.request(
      'delete',
      `${this.baseUrl}/admin/learning-resource/module`,
      {
        headers: this.headers,
        body: payload,
      }
    );
  }


  deleteLearningResourceFolder(payload: { moduleName: string; folderPath: string }): Observable<any> {
    return this.http.request(
      'delete',
      `${this.baseUrl}/admin/learning-resource/folder`,
      {
        headers: this.headers,
        body: payload,
      }
    );
  }




  /**
   * Get learning resources by type
   * @param type - Filter by type: 'pdf', 'video', 'image', 'document'
   */
  getLearningResourcesByType(
    type: 'pdf' | 'video' | 'image' | 'document',
    page = 1,
    limit = 100
  ): Observable<LearningResourcesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('type', type);

    return this.http.get<LearningResourcesResponse>(
      `${this.baseUrl}/common/learning-resources`,
      { params, headers: this.headers }
    );
  }

  /**
   * Load all resources with pagination handling
   * Useful when you need to fetch all items across multiple pages
   */
  async loadAllResources(): Promise<LearningResource[]> {
    const allResources: LearningResource[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await this.getLearningResources(
          currentPage,
          100
        ).toPromise();

        if (response && response.data) {
          allResources.push(...response.data);

          // Check if there are more pages
          hasMore = response.pagination.nextPage !== null;
          currentPage++;
        } else {
          hasMore = false;
        }
      } catch (error) {
        console.error('Error loading resources:', error);
        hasMore = false;
      }
    }

    return allResources;
  }


  // Instructor Profile Update
  updateInstructorProfile(profileData: any, token: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/instructor/profile`, profileData, {
      headers: this.headers,
    });
  }

  getInstructorProfile(token: string): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/instructor/profile`, { headers: this.headers })
      .pipe(
        map((res) => {
          if (res.instructor?.image) {
            res.instructor.image = `${this.baseUrl}/${res.instructor.image}`;
          }
          return res;
        })
      );
  }
  // instructor dashboard
  getDashboardInfo(timeSpentYear: number, earningYear: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/instructor/dashboard?timeSpentYear=${timeSpentYear}&earningYear=${earningYear}`, {
      headers: this.headers,
    });
  }
  getLiveSessions(date: string): Observable<any> {
    const params = new HttpParams().set('date', date);
    return this.http.get<any>(
      `${this.baseUrl}/instructor/dashboard/live-sessions`,
      {
        headers: this.headers,
        params,
      }
    );
  }
  getReviewDetail(reviewId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/instructor/review-detail/${reviewId}`,
      { headers: this.headers }
    );
  }
  // Instructor Bookings
  getExclusiveWebinarBookings(page = 1, limit = 100): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get(
      `${this.baseUrl}/instructor/exclusive-webinar-bookings`,
      { headers: this.headers, params }
    );
  }
  searchExclusiveWebinarBookings(
    query: string,
    page = 1,
    limit = 100
  ): Observable<any> {
    const params = new HttpParams()
      .set('query', query)
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get(
      `${this.baseUrl}/instructor/exclusive-webinar-bookings/search`,
      { headers: this.headers, params }
    );
  }
  // Countries API
  getCountries(): Observable<{ name: string; code: string }[]> {
    return this.http
      .get<any[]>('https://restcountries.com/v3.1/all?fields=name,cca2')
      .pipe(
        map((res: any[]) =>
          res
            .map((c) => ({
              name: c.name.common,
              code: c.cca2,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      );
  }

  // Forums APIs (updated and new methods)
  getForums(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/forum`, {
      headers: this.headers,
    });
  }

  createForum(token: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/forum`, payload, {
      headers: this.headers,
    });
  }

  updateForum(token: string, forumId: string, payload: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/common/forum/${forumId}`, payload, {
      headers: this.headers,
    });
  }

  deleteForum(token: string, forumId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/common/forum/${forumId}`, {
      headers: this.headers,
    });
  }



  updateForumPost(token: string, postId: string, payload: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/common/forum-post/${postId}`, payload, {
      headers: this.headers,
    });
  }

  deleteForumPost(token: string, postId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/common/forum-post/${postId}`, {
      headers: this.headers,
    });
  }

  createPost(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/forum-post`, payload, {
      headers: this.headers,
    });
  }
  getForumCategories(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/forum-categories/all`, {
      headers: this.headers,
    });
  }

  joinForum(token: string, forumId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/forum/join`,
      { forumId },
      { headers: this.headers }
    );
  }

  alreadyJoin(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/joined-forums`, {
      headers: this.headers,
    });
  }

  leaveForum(token: string, forumId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/forum/leave`,
      { forumId },
      { headers: this.headers }
    );
  }

  getForumMembers(
    token: string,
    forumId: string,
    page = 1,
    limit = 10
  ): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/forum/members/${forumId}?page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  getForumById(token: string, forumId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/forum/${forumId}`, {
      headers: this.headers,
    });
  }

  getForumsPaginated(token: string, page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/forum?page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  createForumPost(token: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/forum-post`, payload, {
      headers: this.headers,
    });
  }

  likeUnlikeForumPost(token: string, postId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/forum-post/like-unlike`,
      { id: postId },
      { headers: this.headers }
    );
  }

  getForumPostById(token: string, postId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/forum-single-post/${postId}`,
      { headers: this.headers }
    );
  }

  getForumPostsByForumId(
    token: string,
    forumId: string,
    page = 1,
    limit = 5
  ): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/forum-post/${forumId}?page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  addCommentToPost(
    token: string,
    payload: { postId: string; content: string }
  ): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/forum-post/comment`,
      payload,
      { headers: this.headers }
    );
  }
  likeUnlikeComment(token: string, id: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/forum-post/comment/like-unlike`,
      { id },
      { headers: this.headers }
    );
  }

  // Updated getCommentById to match the new API response structure
  getCommentById(
    token: string,
    postId: string
  ): Observable<{ status: number; count: number; data: Comment[] }> {
    return this.http.get<{ status: number; count: number; data: Comment[] }>(
      `${this.baseUrl}/common/forum-post/comment/${postId}`,
      { headers: this.headers }
    );
  }

  // Updated getCommentReplies to match the new API response structure
  getCommentReplies(
    token: string,
    commentId: string,
    page?: number,
    limit?: number
  ): Observable<{ status: number; count: number; data: Reply[] }> {
    const qp = new URLSearchParams();
    if (page) qp.set('page', String(page));
    if (limit) qp.set('limit', String(limit));
    const qs = qp.toString() ? `?${qp.toString()}` : '';
    return this.http.get<{ status: number; count: number; data: Reply[] }>(
      `${this.baseUrl}/common/comment-reply/${commentId}${qs}`,
      { headers: this.headers }
    );
  }

  likeUnlikeCommentReply(token: string, id: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/comment-reply/like-unlike`,
      { id },
      { headers: this.headers }
    );
  }

  delCommentReply(token: string, replyId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/common/comment-reply/${replyId}`,
      { headers: this.headers }
    );
  }
  delComment(token: string, commentId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/common/comment/${commentId}`,
      { headers: this.headers }
    );
  }

  // Correct reply APIs: proper payloads and HTTP verbs
  replyToComment(
    token: string,
    payload: { commentId: string; content: string }
  ): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/comment-reply`,
      payload,
      { headers: this.headers }
    );
  }

  editCommentReply(
    token: string,
    replyId: string,
    payload: { content: string }
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/common/comment-reply/${replyId}`,
      payload,
      { headers: this.headers }
    );
  }

  // Add comment edit/delete APIs
  editComment(
    token: string,
    commentId: string,
    payload: { content: string }
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/common/forum-post/comment/${commentId}`,
      payload,
      { headers: this.headers }
    );
  }

  deleteComment(token: string, commentId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/common/forum-post/comment/${commentId}`,
      { headers: this.headers }
    );
  }

  getForumStats(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/forum/stats`, {
      headers: this.headers,
    });
  }

  getForumStatsById(token: string, forumId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/forum/stats/${forumId}`, {
      headers: this.headers,
    });
  }

  searchForums(
    token: string,
    query: string,
    page?: number,
    limit?: number
  ): Observable<any> {
    const qp = new URLSearchParams({ query });
    if (page) qp.set('page', String(page));
    if (limit) qp.set('limit', String(limit));
    return this.http.get<any>(
      `${this.baseUrl}/common/forum/search?${qp.toString()}`,
      { headers: this.headers }
    );
  }
  // ask To GPT api
  askToGPT(prompt?: string): Observable<any> {
    const body = { prompt };
    return this.http.post<any>(`${this.baseUrl}/common/askGPT`, body, {
      headers: this.headers,
    });
  }

  // certificate api's
  getCertificates(page?: number, limit?: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/student/certificate?page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  getDetailOfCertificate(certId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/student/certificate/detail/${certId}`,
      { headers: this.headers }
    );
  }

  downloadCertificate(certId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/student/certificate/download/${certId}`,
      {
        headers: this.headers,
        responseType: 'blob',
      }
    );
  }

  // Admin APIs

  // dashboard stats api
  getDashboardSessionStats(
    type: string,
    range: number,
    token: string
  ): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/dashboard-session-stats?type=${type}&range=${range}`,
      { headers: this.headers }
    );
  }

  getDashboardStats(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/dashboard-stats`, {
      headers: this.headers,
    });
  }

  getPlatformEarningsSummary(): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/platform/earnings-summary`, {
      headers: this.headers,
    });
  }

  // ✅ Send "Say Congrats" email
  sayCongrats(data: any, token: string) {
    return this.http.post(`${this.baseUrl}/admin/say-congrats`, data, {
      headers: this.headers,
    });
  }

  sendWelcomeEmail(payload: any) {
    return this.http.post<any>(`${this.baseUrl}/admin/welcome`, payload, {
      headers: this.headers,
    });
  }

  /** 🔹 Get all live sessions by date */
  getLiveSessionsByDate(date: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/dashboard-live-sessions?date=${date}`,
      { headers: this.headers }
    );
  }

  searchLiveSessions(query: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/dashboard-live-sessions/search?query=${query}`,
      { headers: this.headers }
    );
  }

  // 🔹 Free Events APIs
  createEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/event`, payload, {
      headers: this.headers,
    });
  }

  updateEvent(id: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/event/${id}`, payload, {
      headers: this.headers,
    });
  }

  getEventById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/event/${id}`, {
      headers: this.headers,
    });
  }

  deleteEvent(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/event/${id}`, {
      headers: this.headers,
    });
  }

  getAllEvents(page = 1, limit = 10, sortBy = 'desc'): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/events?page=${page}&limit=${limit}&sortBy=${sortBy}`, {
      headers: this.headers,
    });
  }

  getEvents(page = 1, limit = 10, sortBy = 'desc'): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/event?page=${page}&limit=${limit}&sortBy=${sortBy}`,
      { headers: this.headers }
    );
  }

  // 🔹 Barter Events APIs
  createBarterEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/barter-event`, payload, {
      headers: this.headers,
    });
  }

  updateBarterEvent(id: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/barter-event/${id}`, payload, {
      headers: this.headers,
    });
  }

  getBarterEventById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/barter-event/${id}`, {
      headers: this.headers,
    });
  }

  deleteBarterEvent(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/barter-event/${id}`, {
      headers: this.headers,
    });
  }

  getAllBarterEvents(page = 1, limit = 10, sortBy = 'desc'): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/barter-events?page=${page}&limit=${limit}&sortBy=${sortBy}`, {
      headers: this.headers,
    });
  }


  // 🔹 Paid Webinars APIs
  createWebinar(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/exclusive-webinar`, payload, {
      headers: this.headers,
    });
  }

  updateWebinar(id: string, payload: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/exclusive-webinar/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  getWebinarById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/exclusive-webinar/${id}`, {
      headers: this.headers,
    });
  }

  deleteWebinar(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/exclusive-webinar/${id}`, {
      headers: this.headers,
    });
  }

  getWebinars(page = 1, limit = 10, sortBy = 'desc'): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/exclusive-webinar?page=${page}&limit=${limit}&sortBy=${sortBy}`,
      { headers: this.headers }
    );
  }

  // 🟢 Get Free Event Sessions
  getFreeEventSessions(page = 1, limit = 10): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/free-event-sessions?page=${page}&limit=${limit}`, { headers: this.headers });
  }

  // 🟢 Register for Free Event Session
  registerForFreeSession(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/register-for-session`, payload, { headers: this.headers });
  }

  // library apis
  // ✅ CREATE
  createLearningResource(data: LearningResource): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/learning-resource`, data, {
      headers: this.headers,
    });
  }

  getLearningResource(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/learning-resource`, {
      headers: this.headers,
    });
  }

  // ✅ GET BY ID
  getLearningResourceById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/learning-resource/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ UPDATE BY ID
  updateLearningResource(id: string, data: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/learning-resource/${id}`,
      data,
      { headers: this.headers }
    );
  }

  // ✅ DELETE BY ID
  deleteLearningResource(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/learning-resource/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ SEARCH BY TITLE
  searchLearningResource(title: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/search/learning-resource?title=${title}`,
      { headers: this.headers }
    );
  }

  // ✅ create question
  createQuestion(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/question`, payload, {
      headers: this.headers,
    });
  }

  // ✅ get all questions
  getQuestions(): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/question`, {
      headers: this.headers,
    });
  }

  // ✅ create quiz
  createQuiz(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/quiz`, payload, {
      headers: this.headers,
    });
  }
  getQuestionsByType(type: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/question?type=${type}`, {
      headers: this.headers,
    });
  }
  // ✅ GET quiz by ID
  getQuizById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/quiz/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ GET quizzes by type (mixed, short_answer, fill_in_the_blank)
  getQuizzesByType(type: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/quiz?type=${type}`, {
      headers: this.headers,
    });
  }
  updateQuestion(payload: any) {
    return this.http.put(`${this.baseUrl}/admin/question`, payload, {
      headers: this.headers,
    });
  }
  updateQuiz(payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/quiz`, payload, {
      headers: this.headers,
    });
  }

  deleteQuiz(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/quiz/${id}`, {
      headers: this.headers,
    });
  }

  getAllQuizzes(page: number = 1, limit: number = 12, type?: string): Observable<any> {
    const params: any = { page, limit };

    // IMPORTANT:
    // If you want "mixed" to mean "no filter", don't send type=mixed.
    // Only send type when user selects a specific type.
    if (type && type !== 'mixed') params.type = type;

    return this.http.get(`${this.baseUrl}/admin/quiz`, {
      headers: this.headers,
      params
    });
  }

  createPackage(token: string, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/package`, payload, {
      headers: this.headers,
    });
  }

  getAllPackages(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/package`, {
      headers: this.headers,
    });
  }

  updatePackage(id: string, data: any, token: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/package/${id}`, data, {
      headers: this.headers,
    });
  }

  deletePackage(id: string, token: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/package/${id}`, {
      headers: this.headers,
    });
  }
  getPackageDetail(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/package/${id}`, {
      headers: this.headers,
    });
  }
  searchPackages(name: string, page: number, limit: number): Observable<any> {
    let params = new HttpParams()
      .set('name', name)
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get(`${this.baseUrl}/admin/package/search`, {
      headers: this.headers,
      params,
    });
  }



  createInstallmentPlan(payload: any, token: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/installment-plan`, payload, {
      headers: this.headers,
    });
  }

  getInstallmentPlans(packageId: string, token: string): Observable<any> {
    const params = new HttpParams().set('packageId', packageId);
    return this.http.get(`${this.baseUrl}/admin/installment-plan`, {
      headers: this.headers,
      params,
    });
  }

  getInstallmentPlanById(id: string, token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/installment-plan/${id}`, {
      headers: this.headers,
    });
  }

  updateInstallmentPlan(id: string, payload: any, token: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/installment-plan/${id}`, payload, {
      headers: this.headers,
    });
  }

  deleteInstallmentPlan(id: string, token: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/installment-plan/${id}`, {
      headers: this.headers,
    });
  }




  createPackageDiscountCampaign(payload: any, token: string): Observable<any> {
  return this.http.post(`${this.baseUrl}/admin/package-discount-campaign`, payload, {
    headers: this.headers,
  });
}

getPackageDiscountCampaigns(packageId?: string, status?: string, token?: string): Observable<any> {
  let params = new HttpParams();

  if (packageId) {
    params = params.set('packageId', packageId);
  }

  if (status) {
    params = params.set('status', status);
  }

  return this.http.get(`${this.baseUrl}/admin/package-discount-campaign`, {
    headers: this.headers,
    params,
  });
}

getPackageDiscountCampaignById(id: string, token: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/package-discount-campaign/${id}`, {
    headers: this.headers,
  });
}

updatePackageDiscountCampaign(id: string, payload: any, token: string): Observable<any> {
  return this.http.patch(`${this.baseUrl}/admin/package-discount-campaign/${id}`, payload, {
    headers: this.headers,
  });
}

deletePackageDiscountCampaign(id: string, token: string): Observable<any> {
  return this.http.delete(`${this.baseUrl}/admin/package-discount-campaign/${id}`, {
    headers: this.headers,
  });
}

getActivePackageDiscountCampaign(packageId: string, token: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/package-discount-campaign/${packageId}/active`, {
    headers: this.headers,
  });
}

  updatePackageLearningPathCard(
    id: string,
    payload: { learningPathSubtitle?: string; learningPathBullets?: string[] },
    token?: string
  ): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/admin/packages/${id}/learning-path-card`,
      payload,
      { headers: this.headers }
    );
  }


 
  //get transaction detail in admin
  getTransactionInAdmin(page: number, limit: number): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get(`${this.baseUrl}/admin/platform/transactions`, {
      headers: this.headers,
      params,
    });
  }

 // ✅ Email Templates (NEW APIs)

getEmailTemplates(): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/email/template`, { headers: this.headers });
}

getEmailTemplateByKey(key: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}`, { headers: this.headers });
}

updateEmailTemplateByKey(key: string, payload: any): Observable<any> {
  return this.http.patch(`${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}`, payload, { headers: this.headers });
}

getEmailTemplateHistory(key: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}/history`, { headers: this.headers });
}

restoreEmailTemplateVersion(key: string, version: number): Observable<any> {
  return this.http.post(`${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}/restore/${version}`, {}, { headers: this.headers });
}

previewEmailTemplate(key: string, context: any): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}/preview`,
    { context },
    { headers: this.headers }
  );
}

testSendEmailTemplate(key: string, to: string, context: any): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/email/template/${encodeURIComponent(key)}/test-send`,
    { to, context },
    { headers: this.headers }
  );
}
  // ✅ POST - Create Category
  createForumCategory(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/forum-category`, payload, {
      headers: this.headers,
    });
  }

  // ✅ PUT - Update Category
  updateForumCategory(id: string, payload: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/forum-category/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  // ✅ DELETE - Delete Category
  deleteForumCategory(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/forum-category/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ GET - Get by ID
  getForumCategoryById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/forum-category/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ GET - Get all categories with pagination + sort
  getAllForumCategories(
    page: number,
    limit: number,
    sortBy: 'asc' | 'desc'
  ): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/forum-category?page=${page}&limit=${limit}&sortBy=${sortBy}`,
      {
        headers: this.headers,
      }
    );
  }

  // ✅ GET - Search categories
  searchForumCategories(
    query: string,
    page: number,
    limit: number
  ): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/forum-category/search?query=${query}&page=${page}&limit=${limit}`,
      {
        headers: this.headers,
      }
    );
  }

  // admin course
  //  categories of courses admin
  createMainCategory(payload: any) {
    return this.http.post(
      `${this.baseUrl}/admin/course-main-category`,
      payload,
      { headers: this.headers }
    );
  }

  createSubCategory(payload: any) {
    return this.http.post(
      `${this.baseUrl}/admin/course-sub-category`,
      payload,
      { headers: this.headers }
    );
  }

  // --- LIST (with page, limit, sortBy) ---
  getMainCategories(paramsObj: any = {}) {
    let params = new HttpParams();
    Object.keys(paramsObj).forEach((k) => {
      if (
        paramsObj[k] !== undefined &&
        paramsObj[k] !== null &&
        paramsObj[k] !== ''
      ) {
        params = params.set(k, paramsObj[k]);
      }
    });
    return this.http.get(`${this.baseUrl}/admin/course-main-category`, {
      headers: this.headers,
      params,
    });
  }

  getSubCategories(paramsObj: any = {}) {
    let params = new HttpParams();
    Object.keys(paramsObj).forEach((k) => {
      if (
        paramsObj[k] !== undefined &&
        paramsObj[k] !== null &&
        paramsObj[k] !== ''
      ) {
        params = params.set(k, paramsObj[k]);
      }
    });
    return this.http.get(`${this.baseUrl}/admin/course-sub-category`, {
      headers: this.headers,
      params,
    });
  }

  // --- SEARCH (separate endpoints) ---
  searchMainCategories(name: string, paramsObj: any = {}) {
    let params = new HttpParams().set('name', name);
    Object.keys(paramsObj).forEach((k) => {
      if (
        paramsObj[k] !== undefined &&
        paramsObj[k] !== null &&
        paramsObj[k] !== ''
      ) {
        params = params.set(k, paramsObj[k]);
      }
    });
    return this.http.get(`${this.baseUrl}/admin/course-main-category/search`, {
      headers: this.headers,
      params,
    });
  }

  searchSubCategories(name: string, paramsObj: any = {}) {
    let params = new HttpParams().set('name', name);
    Object.keys(paramsObj).forEach((k) => {
      if (
        paramsObj[k] !== undefined &&
        paramsObj[k] !== null &&
        paramsObj[k] !== ''
      ) {
        params = params.set(k, paramsObj[k]);
      }
    });
    return this.http.get(`${this.baseUrl}/admin/course-sub-category/search`, {
      headers: this.headers,
      params,
    });
  }

  // --- GET BY ID ---
  getMainCategoryById(id: string) {
    return this.http.get(`${this.baseUrl}/admin/course-main-category/${id}`, {
      headers: this.headers,
    });
  }

  getSubCategoryById(id: string) {
    return this.http.get(`${this.baseUrl}/admin/course-sub-category/${id}`, {
      headers: this.headers,
    });
  }

  // --- UPDATE ---
  updateMainCategory(id: string, payload: any) {
    return this.http.put(
      `${this.baseUrl}/admin/course-main-category/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  updateSubCategory(id: string, payload: any) {
    return this.http.put(
      `${this.baseUrl}/admin/course-sub-category/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  // --- DELETE ---
  deleteMainCategory(id: string) {
    return this.http.delete(
      `${this.baseUrl}/admin/course-main-category/${id}`,
      { headers: this.headers }
    );
  }

  deleteSubCategory(id: string) {
    return this.http.delete(`${this.baseUrl}/admin/course-sub-category/${id}`, {
      headers: this.headers,
    });
  }

  getCourses(
    page: number = 1,
    limit: number = 10,
    sortBy: string = 'asc',
    search: string = ''
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('sortBy', sortBy)
      .set('search', search);

    return this.http.get(`${this.baseUrl}/admin/course`, {
      headers: this.headers,
      params,
    });
  }

  getCourseById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/course/${id}`, {
      headers: this.headers,
    });
  }

  addCourse(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/course`, payload, {
      headers: this.headers,
    });
  }

  updateCourse(id: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/course/${id}`, payload, {
      headers: this.headers,
    });
  }

  deleteCourse(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/course/${id}`, {
      headers: this.headers,
    });
  }

  getAllInstructors(paramsObj: any = {}) {
    let params = new HttpParams();
    Object.keys(paramsObj).forEach((key) => {
      if (
        paramsObj[key] !== undefined &&
        paramsObj[key] !== null &&
        paramsObj[key] !== ''
      ) {
        params = params.set(key, paramsObj[key]);
      }
    });

    return this.http.get(`${this.baseUrl}/admin/all-instructors`, {
      headers: this.headers,
      params,
    });
  }

  // admin blogs

  // ✅ Upload image API
  uploadFiles(token: String, formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/uploadFiles`, formData, {
      headers: this.headers,
    });
  }


  uploadFilesMultiple(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/uploadFilesMultiple`,
      formData,
      { headers: this.headers }
    );
  }



  // ✅ Upload image API
  uploadFilesWithIndicator(token: String, formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/common/uploadFilesWithIndicator`, formData, {
      headers: this.headers,
    });
  }


  uploadFilesMultipleWithIndicator(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/common/uploadFilesMultipleWithIndicator`,
      formData,
      { headers: this.headers }
    );
  }

  uploadFilesMultipleWithIndicatorProgress(
  formData: FormData,
  onProgress: (pct: number) => void
): Promise<any> {
  const url = `${this.baseUrl}/common/uploadFilesMultipleWithIndicator?progress=1`;

  const auth =
    (this.headers as any)?.get?.('Authorization') ||
    (this.headers as any)?.get?.('authorization');

  const fetchHeaders: Record<string, string> = {
    Accept: 'text/event-stream'
  };
  if (auth) fetchHeaders['Authorization'] = auth;

  return fetch(url, {
    method: 'POST',
    body: formData,
    headers: fetchHeaders,
  }).then(async (res) => {
    if (!res.ok) {
      let msg = 'Upload failed';
      try {
        const j = await res.json();
        msg = j?.error || j?.message || msg;
      } catch {}
      throw new Error(msg);
    }

    const contentType = res.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const j = await res.json();
      onProgress(100);
      return j;
    }

    if (!res.body) throw new Error('No response stream');

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');

    let buffer = '';
    let lastPct = 0;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const chunk of parts) {
        const lines = chunk.split('\n');
        const eventLine = lines.find(l => l.startsWith('event:'));
        const dataLine = lines.find(l => l.startsWith('data:'));

        const event = eventLine?.slice(6).trim();
        const dataStr = dataLine?.slice(5).trim();

        if (!event || !dataStr) continue;

        let payload: any;
        try { payload = JSON.parse(dataStr); } catch { continue; }

        if (event === 'progress') {
          const pct = Number(payload?.percent ?? 0);
          const safe = Number.isFinite(pct)
            ? Math.max(0, Math.min(100, Math.floor(pct)))
            : 0;

          lastPct = Math.max(lastPct, safe);
          onProgress(lastPct);
        }

        if (event === 'done') {
          onProgress(100);
          return payload;
        }

        if (event === 'error') {
          throw new Error(payload?.error || 'Upload failed');
        }
      }
    }

    throw new Error('Upload stream ended unexpectedly');
  });
}



uploadFilesMultipleWithClientProgress(
  formData: FormData,
  onProgress: (pct: number) => void
): Promise<any> {
  const url = `${this.baseUrl}/common/uploadFilesMultipleWithIndicator`;

  // ✅ Convert your plain object headers -> HttpHeaders
  const httpHeaders = new HttpHeaders(this.headers as any);

  const req = new HttpRequest('POST', url, formData, {
    headers: httpHeaders,
    reportProgress: true,
    responseType: 'json'
  });

  const obs$ = this.http.request(req).pipe(
    tap((event: HttpEvent<any>) => {
      if (event.type === HttpEventType.Sent) onProgress(0);

      if (event.type === HttpEventType.UploadProgress) {
        const total = event.total ?? 0;
        const loaded = event.loaded ?? 0;
        const pct = total > 0 ? Math.floor((loaded / total) * 100) : 0;
        onProgress(Math.max(0, Math.min(100, pct)));
      }
    }),
    filter((event: HttpEvent<any>) => event.type === HttpEventType.Response)
  );

  return firstValueFrom(obs$).then((event) => {
    onProgress(100);
    return (event as HttpResponse<any>).body;
  });
}



  // ✅ Create new blog API
  addBlog(token: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/admin/blog`, payload, {
      headers: this.headers,
    });
  }

  // ✅ Get all blogs (requires token)
  getAllBlogs(token: string, page: number = 1, limit: number = 10, sortBy: string = 'desc'): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/blog?page=${page}&limit=${limit}&sortBy=${sortBy}`,
      { headers: this.headers }
    );
  }


  updateBlog(blogId: string, payload: any, token: string) {
    return this.http.put(`${this.baseUrl}/admin/blog/${blogId}`, payload, {
      headers: this.headers,
    });
  }

  getBlogByIdAdmin(token: string, blogId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/blog/${blogId}`, {
      headers: this.headers,
    });
  }


  deleteBlog(blogId: string, token: string) {
    return this.http.delete(`${this.baseUrl}/admin/blog/${blogId}`, {
      headers: this.headers,
    });
  }

  /** ✅ 1. Get all (with pagination + sorting) */
  getAllBlogMainCategories(
    token: string,
    page = 1,
    limit = 10,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<any> {
    const url = `${this.baseUrl}/admin/blog-main-category?page=${page}&limit=${limit}&sortBy=${sortBy}`;
    return this.http.get(url, { headers: this.headers });
  }

  /** ✅ 2. Search categories (with pagination) */
  searchBlogMainCategories(
    token: string,
    name: string,
    page = 1,
    limit = 10
  ): Observable<any> {
    const url = `${this.baseUrl}/admin/blog-main-category/search?name=${name}&page=${page}&limit=${limit}`;
    return this.http.get(url, { headers: this.headers });
  }

  // ✅ Create new category
  addBlogMainCategory(data: any, token: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/blog-main-category`, data, {
      headers: this.headers,
    });
  }

  // ✅ Update category
  updateBlogMainCategory(id: string, data: any, token: string): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/blog-main-category/${id}`,
      data,
      { headers: this.headers }
    );
  }

  // ✅ Delete category
  deleteBlogMainCategory(id: string, token: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/blog-main-category/${id}`, {
      headers: this.headers,
    });
  }



  /** ✅ 1. Get all (with pagination + sorting) */
  getAllBlogSubCategories(
    token: string,
    page = 1,
    limit = 10,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<any> {
    const url = `${this.baseUrl}/admin/blog-sub-category?page=${page}&limit=${limit}&sortBy=${sortBy}`;
    return this.http.get(url, { headers: this.headers });
  }

  /** ✅ 2. Search categories (with pagination) */
  searchBlogSubCategories(
    token: string,
    name: string,
    page = 1,
    limit = 10
  ): Observable<any> {
    const url = `${this.baseUrl}/admin/blog-sub-category/search?name=${name}&page=${page}&limit=${limit}`;
    return this.http.get(url, { headers: this.headers });
  }

  // ✅ Create new category
  addBlogSubCategory(data: any, token: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/blog-sub-category`, data, {
      headers: this.headers,
    });
  }

  // ✅ Update category
  updateBlogSubCategory(id: string, data: any, token: string): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/blog-sub-category/${id}`,
      data,
      { headers: this.headers }
    );
  }

  // ✅ Delete category
  deleteBlogSubCategory(id: string, token: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/blog-sub-category/${id}`, {
      headers: this.headers,
    });
  }

  // ✅ Get all badges (with pagination & sort)
  getAllBadges(token: string, page: number, limit: number, sortBy: string) {
    return this.http.get<any>(
      `${this.baseUrl}/admin/badge?page=${page}&limit=${limit}&sortBy=${sortBy}`,
      { headers: this.headers }
    );
  }

  searchBadges(query: string, page: number, limit: number) {
    return this.http.get<any>(
      `${this.baseUrl}/admin/badge/search?query=${encodeURIComponent(
        query
      )}&page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  addBadge(token: string, data: any) {
    return this.http.post<any>(`${this.baseUrl}/admin/badge`, data, {
      headers: this.headers,
    });
  }

  updateBadge(token: string, id: string, data: any) {
    return this.http.put<any>(`${this.baseUrl}/admin/badge/${id}`, data, {
      headers: this.headers,
    });
  }

  deleteBadge(token: string, id: string) {
    return this.http.delete<any>(`${this.baseUrl}/admin/badge/${id}`, {
      headers: this.headers,
    });
  }

  // Booking API'S
  // Get webinar resources by webinar ID
  getWebinarResources(webinarId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/exclusive-webinar-resources/${webinarId}`,
      { headers: this.headers }
    );
  }

  // Book a webinar
  bookWebinar(payload: {
    webinarId: string;
    amountPaid?: number;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/common/bookings`, payload, {
      headers: this.headers,
    });
  }


  getDailyWebinarBookings(date: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/bookings`, {
      headers: this.headers,
      params: { filterType: 'daily', date },
    });
  }

  // Student Calendar (single fetch API like bookings)
  getStudentCalendar(params: {
    filterType: 'daily' | 'weekly' | 'monthly';
    date?: string;        // YYYY-MM-DD for daily
    startDate?: string;   // YYYY-MM-DD for weekly
    endDate?: string;     // YYYY-MM-DD for weekly
    month?: string;       // "December"
    year?: number;        // 2025
  }): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/studentCalendar`, {
      headers: this.headers,
      params: params as any,
    });
  }

  // Create custom calendar item (student only)
  createStudentCalendarItem(payload: {
    title: string;
    description?: string;
    type: 'event' | 'reminder' | 'todo';
    sessions: { startTime: string; endTime: string; zoomLink?: string | null }[];
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/studentCalendar`, payload, {
      headers: this.headers,
    });
  }

  // Update custom calendar item (student only)
  updateStudentCalendarItem(
    id: string,
    payload: {
      title?: string;
      description?: string;
      type?: 'event' | 'reminder' | 'todo';
      sessions?: { startTime: string; endTime: string; zoomLink?: string | null }[];
    }
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/student/studentCalendar/${id}`, payload, {
      headers: this.headers,
    });
  }

  // Delete custom calendar item (student only)
  deleteStudentCalendarItem(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/student/studentCalendar/${id}`, {
      headers: this.headers,
    });
  }


  // Instructor Calendar (single fetch API like bookings)
  getInstructorCalendar(params: {
    filterType: 'daily' | 'weekly' | 'monthly';
    date?: string;        // YYYY-MM-DD for daily
    startDate?: string;   // YYYY-MM-DD for weekly
    endDate?: string;     // YYYY-MM-DD for weekly
    month?: string;       // "December"
    year?: number;        // 2025
  }): Observable<any> {
    return this.http.get(`${this.baseUrl}/instructor/instructorCalendar`, {
      headers: this.headers,
      params: params as any,
    });
  }

  // Create custom calendar item (student only)
  createInstructorCalendarItem(payload: {
    title: string;
    description?: string;
    type: 'event' | 'reminder' | 'todo';
    sessions: { startTime: string; endTime: string; zoomLink?: string | null }[];
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/instructor/instructorCalendar`, payload, {
      headers: this.headers,
    });
  }

  // Update custom calendar item (student only)
  updateInstructorCalendarItem(
    id: string,
    payload: {
      title?: string;
      description?: string;
      type?: 'event' | 'reminder' | 'todo';
      sessions?: { startTime: string; endTime: string; zoomLink?: string | null }[];
    }
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/instructor/instructorCalendar/${id}`, payload, {
      headers: this.headers,
    });
  }

  // Delete custom calendar item (student only)
  deleteInstructorCalendarItem(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/instructor/instructorCalendar/${id}`, {
      headers: this.headers,
    });
  }


  // Get webinar bookings for weekly view
  getWeeklyWebinarBookings(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/bookings`, {
      headers: this.headers,
      params: { filterType: 'weekly', startDate, endDate },
    });
  }

  // Get webinar bookings for monthly view
  getMonthlyWebinarBookings(month: string, year: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/bookings`, {
      headers: this.headers,
      params: { filterType: 'monthly', month, year },
    });
  }

  // Get all webinar bookings for student
  getWebinarBookings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/bookings`, {
      headers: this.headers,
    });
  }

  // cancel booking
  cancelBooking(): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/exclusive-webinar/cancel`,
      {},
      {
        headers: this.headers,
      }
    );
  }
  // Barter Coaching API's
  allEvents(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/barter-events`, {
      headers: this.headers,
    });
  }

  getJoinedEvents(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/barter-joinEvents`, {
      headers: this.headers,
    });
  }

  joinEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/barter-events/join`, payload, {
      headers: this.headers,
    });
  }

  startBarter(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/barter-events/start-barter`,
      payload,
      { headers: this.headers }
    );
  }

  logBarterTime(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/barter-events/barter-log`,
      payload,
      { headers: this.headers }
    );
  }

  barterLog(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/barter-events/barter-log`,
      payload,
      { headers: this.headers }
    );
  }



  // Live Sessions API's
  allLiveEvents(page = 1, limit = 30, sortBy = 'asc'): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get(`${this.baseUrl}/student/live-events`, {
      params,
      headers: this.headers,
    });
  }
  allLiveEventsWithStatus(page = 1, limit = 30, sortBy = 'asc'): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get(`${this.baseUrl}/student/live-events-with-status`, {
      params,
      headers: this.headers,
    });
  }

  allInstructorLiveEventsWithStatus(page = 1, limit = 30, sortBy = 'asc'): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get(`${this.baseUrl}/instructor/live-events-with-status`, {
      params,
      headers: this.headers,
    });
  }

  getJoinedLiveEvents(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/joinedLiveEvents`, {
      headers: this.headers,
    });
  }

  joinLiveEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/joinLiveEvent`, payload, {
      headers: this.headers,
    });
  }


  unregisterLiveEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/unregister-live-event`, payload, {
      headers: this.headers,
    });
  }


  // Live Sessions API's
  allInstructorLiveEvents(page = 1, limit = 30, sortBy = 'asc'): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get(`${this.baseUrl}/instructor/live-events`, {
      params,
      headers: this.headers,
    });
  }

  getInstructorJoinedLiveEvents(): Observable<any> {
    return this.http.get(`${this.baseUrl}/instructor/joinedLiveEvents`, {
      headers: this.headers,
    });
  }

  joinInstructorLiveEvent(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/instructor/joinLiveEvent`, payload, {
      headers: this.headers,
    });
  }

  // startBarter(payload: any): Observable<any> {
  //   return this.http.post(
  //     `${this.baseUrl}/student/barter-events/start-barter`,
  //     payload,
  //     { headers: this.headers }
  //   );
  // }

  // logBarterTime(payload: any): Observable<any> {
  //   return this.http.post(
  //     `${this.baseUrl}/student/barter-events/barter-log`,
  //     payload,
  //     { headers: this.headers }
  //   );
  // }

  // barterLog(payload: any): Observable<any> {
  //   return this.http.post(
  //     `${this.baseUrl}/student/barter-events/barter-log`,
  //     payload,
  //     { headers: this.headers }
  //   );
  // }

  getEventDetails(eventId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/event/${eventId}`, {
      headers: this.headers,
    });
  }
  // Dispute APIs
  createDispute(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/dispute`, payload, {
      headers: this.headers,
    });
  }

  getDisputeDetails(disputeId: string): Observable<any> {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    };
    return this.http.get(`${this.baseUrl}/student/dispute/${disputeId}`, {
      headers,
    });
  }

  getDisputes(
    page: number = 1,
    limit: number = 8,
    status?: string,
    sortBy: 'asc' | 'desc' = 'asc'
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get(`${this.baseUrl}/student/dispute`, {
      headers: this.headers,
      params,
    });
  }
  // Inbox api's
  // Chat APIs
  getChatSearch(
    query: string,
    page?: number,
    limit?: number
  ): Observable<ChatSearchResponse> {
    const params = new HttpParams().set('query', query);

    if (page) params.set('page', page.toString());
    if (limit) params.set('limit', limit.toString());

    return this.http.get<ChatSearchResponse>(
      `${this.baseUrl}/common/chat/search`,
      { headers: this.headers, params }
    );
  }

  getChatConversations(
    page?: number,
    limit?: number
  ): Observable<ConversationsResponse> {
    const params = new HttpParams();
    if (page) params.set('page', page.toString());
    if (limit) params.set('limit', limit.toString());

    return this.http.get<ConversationsResponse>(
      `${this.baseUrl}/common/chat/conversations`,
      { headers: this.headers, params }
    );
  }

  getChatMessages(
    conversationId: string,
    page?: number,
    limit?: number
  ): Observable<ChatSearchResponse> {
    let url = `${this.baseUrl}/common/chat/messages/${conversationId}`;
    const params = new HttpParams();

    if (page) params.set('page', page.toString());
    if (limit) params.set('limit', limit.toString());

    if (page || limit) {
      url += `?${params.toString()}`;
    }

    return this.http.get<ChatSearchResponse>(url, { headers: this.headers });
  }

  // Convenience method to get recent messages for a conversation
  getRecentChatMessages(
    conversationId: string,
    limit: number = 50
  ): Observable<ChatSearchResponse> {
    return this.getChatMessages(conversationId, 1, limit);
  }

  // Method to get conversations with unread messages count
  getConversationsWithUnread(
    page: number = 1,
    limit: number = 20
  ): Observable<ConversationsResponse> {
    return this.getChatConversations(page, limit);
  }

  // Search method that can be used for both messages and conversations
  searchChat(
    query: string,
    type: 'messages' | 'conversations' = 'messages',
    page?: number,
    limit?: number
  ): Observable<any> {
    if (type === 'conversations') {
      return this.getChatSearch(query, page, limit);
    } else {
      // For messages search, you might need a different endpoint or conversation context
      // This is a placeholder - adjust based on your actual API
      return this.getChatSearch(query, page, limit);
    }
  }

  // Helper method to mark messages as read (if you have such an endpoint)
  // markMessagesAsRead(conversationId: string, messageIds: string[]): Observable<any> {

  //   return this.http.post(
  //     `${this.baseUrl}/common/chat/mark-read/${conversationId}`,
  //     { messageIds },
  //     { headers: this.headers }
  //   );
  // }

  // Get conversation by participant ID
  getConversationByParticipant(
    participantId: string
  ): Observable<ConversationsResponse> {
    return this.http.get<ConversationsResponse>(
      `${this.baseUrl}/common/chat/conversations?participant=${participantId}`,
      { headers: this.headers }
    );
  }
  // New methods for message edit and delete
  editMessage(messageId: string, message: string): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/common/chat/message/${messageId}`,
      { message },
      { headers: this.headers }
    );
  }

  deleteMessage(messageId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/common/chat/message/${messageId}`,
      {
        headers: this.headers,
      }
    );
  }
  // CREATE certificate
  createCertificate(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/admin/certificate`, payload, {
      headers: this.headers,
    });
  }

  // GET certificates with pagination
  getCertificatesAdmin(page: number = 1, limit: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    return this.http.get<any>(`${this.baseUrl}/admin/certificate`, {
      headers: this.headers,
      params,
    });
  }

  getDetailOfCertificateAdmin(certId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/certificate/${certId}`, {
      headers: this.headers,
    });
  }

  // certificate api's
  getCertificatesAdminByPage(page?: number, limit?: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/certificate?page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  // GET assigned certificates list with pagination
  getAssignedCertificates(page = 1, limit = 8): Observable<any> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    return this.http.get<any>(`${this.baseUrl}/admin/certificate/assigned`, {
      headers: this.headers,
      params,
    });
  }
  // Create assignment
  createAssignment(payload: Assignment): Observable<Assignment> {
    return this.http.post<Assignment>(
      `${this.baseUrl}/admin/assignment`,
      payload,
      { headers: this.headers }
    );
  }

  // Get all assignments
  getAllAssignments(): Observable<Assignment[]> {
    return this.http.get<Assignment[]>(`${this.baseUrl}/admin/assignment`, {
      headers: this.headers,
    });
  }

  // Get assignment by ID
  getAssignmentById(id: string): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.baseUrl}/admin/assignment/${id}`, {
      headers: this.headers,
    });
  }

  // Update assignment by ID
  updateAssignment(id: string, payload: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(
      `${this.baseUrl}/admin/assignment/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  // Delete assignment by ID
  deleteAssignment(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/assignment/${id}`, {
      headers: this.headers,
    });
  }

  // ASSIGN certificate to students
  assignCertificate(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/certificate/assign`,
      payload,
      { headers: this.headers }
    );
  }

  // UPDATE certificate
  updateCertificate(id: string, payload: Partial<any>): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/admin/certificate/${id}`,
      payload,
      { headers: this.headers }
    );
  }

  // DELETE certificate
  deleteCertificate(id: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/certificate/${id}`, {
      headers: this.headers,
    });
  }

  // (Optional) GET certificate by id
  getCertificateById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/certificate/${id}`, {
      headers: this.headers,
    });
  }

  // package's api

  getPackages(): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/packages`, {
      headers: this.headers,
    });
  }

  // Payment APIs
  /**
   * Create a checkout session for payment
   * @param payload - The payload containing payment details (e.g., webinarId, amount)
   */
  createCheckoutSession(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/create-checkout-session`,
      payload,
      { headers: this.headers }
    );
  }

  /**
   * Verify a checkout session
   * @param sessionId - The Stripe checkout session ID
   */
  verifyCheckoutSession(sessionId: string): Observable<any> {
    const params = new HttpParams().set('session_id', sessionId);
    return this.http.post(
      `${this.baseUrl}/student/verify-checkout`,
      {},
      { headers: this.headers, params }
    );
  }
  verifyInstallmentPay(sessionId: string): Observable<any> {
    const params = new HttpParams().set('session_id', sessionId);
    return this.http.post(
      `${this.baseUrl}/student/verify-installment`,
      {},
      { headers: this.headers, params }
    );
  }
  // installment api's
  getInstallmentDetails(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/installments`, {
      headers: this.headers,
    });
  }
  payInstallment(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/student/pay-installment`, payload, {
      headers: this.headers,
    });
  }
  getTransactionHistory(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/transactions`, {
      headers: this.headers,
    });
  }
  getInstallmentPlan(packageId: string): Observable<any> {
    const params = new HttpParams().set('packageId', packageId);
    return this.http.get(`${this.baseUrl}/common/installment-plans`, {
      headers: this.headers,
      params,
    });
  }

  getInstallmentSummary(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/installment-summary`, {
      headers: this.headers,
    });
  }





  // ✅ Upgrade APIs
  getUpgradeQuote(targetPackageId: string): Observable<any> {
    const params = new HttpParams().set('targetPackageId', targetPackageId);
    return this.http.get(`${this.baseUrl}/student/upgrade-quote`, {
      headers: this.headers,
      params,
    });
  }

  createUpgradeSession(targetPackageId: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/student/upgrade/create-session`,
      { targetPackageId },
      { headers: this.headers }
    );
  }

  /**
   * Stripe will redirect back with ?session_id=...
   * Your backend route is GET /student/upgrade/verify
   */
  verifyUpgrade(sessionId: string): Observable<any> {
    const params = new HttpParams().set('session_id', sessionId);
    return this.http.get(`${this.baseUrl}/student/upgrade/verify`, {
      headers: this.headers,
      params,
    });
  }



  getAccessContent(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/access-content`, {
      headers: this.headers,
    });
  }



  getCurrentPackage(): Observable<any> {
    return this.http.get(`${this.baseUrl}/student/current-package`, {
      headers: this.headers,
    });
  }


  // isntructor payment api's
  getInvoice(page: number, limit: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/instructor/invoice?page=${page}&limit=${limit}`, {
      headers: this.headers,
    });
  }
  getInvoiceDetail(INV: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/instructor/invoice/${INV}`, {
      headers: this.headers,
    });
  }
  addInvoice(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/instructor/invoice`,
      payload,
      {
        headers: this.headers,
      }
    );
  }
  // Admin Dispute APIs
  getAdminDisputes(
    page: number = 1,
    limit: number = 10,
    status?: string,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get(`${this.baseUrl}/admin/dispute`, {
      headers: this.headers,
      params,
    });
  }

  getAdminDisputeById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/dispute/${id}`, {
      headers: this.headers,
    });
  }

  updateAdminDisputeStatus(
    id: string,
    payload: { status: string }
  ): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/dispute/status/${id}`,
      payload,
      {
        headers: this.headers,
      }
    );
  }
  // identity verification api
  getIdentityVerification(
    page: number = 1,
    limit: number = 10,
    identityVerified?: boolean,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    if (identityVerified !== undefined) {
      params = params.set('identityVerified', identityVerified.toString());
    }

    return this.http.get(`${this.baseUrl}/admin/all-users`, {
      headers: this.headers,
      params,
    });
  }
  updateStatusOfUserInAdmin(body: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/users/status`, body, {
      headers: this.headers,
    });
  }
  // GET: All Payment Methods
  getPaymentMethods(): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/payment-method`, {
      headers: this.headers,
    });
  }

  // GET: Single Payment Method by ID
  getPaymentMethodById(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/payment-method/${id}`, {
      headers: this.headers,
    });
  }

  // POST: Create New Payment Method
  createPaymentMethod(data: PaymentMethod): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/payment-method`, data, {
      headers: this.headers,
    });
  }

  // PUT: Update Payment Method
  updatePaymentMethod(
    id: string,
    data: Partial<PaymentMethod>
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/admin/payment-method/${id}`, data, {
      headers: this.headers,
    });
  }

  // DELETE: Remove Payment Method
  deletePaymentMethod(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/payment-method/${id}`, {
      headers: this.headers,
    });
  }
  getAllStudents(
    page: number,
    limit: number,
    sortBy: 'asc' | 'desc' = 'asc'
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    const url = `${this.baseUrl}/admin/all-students`;
    return this.http.get<any>(url, { headers: this.headers, params });
  }

  /* --------------------------------------------------------------
   2. GET ALL INSTRUCTORS
   -------------------------------------------------------------- */
  getAllAdminInstructors(
    page: number,
    limit: number,
    options?: {
      search?: string;
      status?: 'active' | 'pending' | 'inactive' | 'all';
      sortBy?: 'createdAt' | 'name' | 'status' | 'yearsOfExperience' | 'yearsExp';
      sortOrder?: 'asc' | 'desc';
      emailVerified?: boolean | 'all';
      identityVerified?: boolean | 'all';
    }
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    const search = (options?.search || '').trim();
    if (search) params = params.set('search', search);

    // status (don’t send when all)
    if (options?.status && options.status !== 'all') {
      params = params.set('status', options.status);
    }

    // backend expects sortBy + sortOrder (per backend update)
    const sortBy = options?.sortBy || 'createdAt';
    const sortOrder = options?.sortOrder || 'desc';
    params = params.set('sortBy', sortBy).set('sortOrder', sortOrder);

    // optional filters
    if (options?.emailVerified !== undefined && options.emailVerified !== 'all') {
      params = params.set('emailVerified', String(options.emailVerified));
    }
    if (options?.identityVerified !== undefined && options.identityVerified !== 'all') {
      params = params.set('identityVerified', String(options.identityVerified));
    }

    const url = `${this.baseUrl}/admin/all-instructors`;
    return this.http.get<any>(url, { params, headers: this.headers });
  }


  createInstructor(payload: any): Observable<any> {
    const url = `${this.baseUrl}/admin/create-instructor`;
    return this.http.post<any>(url, payload, {
      headers: this.headers,
    });
  }
  // Update in CommonService (add params for pagination)
  getallEvents(
    page: number = 1,
    limit: number = 1000,
    sortBy: 'asc' | 'desc' = 'asc'
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);
    return this.http.get(`${this.baseUrl}/student/allevents`, {
      headers: this.headers,
      params,
    });
  }


  getAdminInvoices(page = 1, limit = 15, search: string = '', status: string = '', sortBy: string = 'desc') {
    const params: string[] = [
      `page=${page}`,
      `limit=${limit}`,
      `sortBy=${encodeURIComponent(sortBy)}`
    ];

    const q = (search || '').trim();
    if (q) params.push(`search=${encodeURIComponent(q)}`);

    const s = (status || '').trim();
    if (s) params.push(`status=${encodeURIComponent(s)}`);

    return this.http.get(`${this.baseUrl}/admin/invoices?${params.join('&')}`, {
      headers: this.headers
    });
  }

  adminInvoicePay(invoiceId: string, payload: any) {
    return this.http.post(`${this.baseUrl}/admin/invoices/pay/${invoiceId}`, payload, {
      headers: this.headers
    });
  }


  getAdminInvoiceDetail(invoiceNumber: string) {
    return this.http.get<any>(`${this.baseUrl}/admin/invoices/${invoiceNumber}`, {
      headers: this.headers
    });
  }
  getReferrals() {
    return this.http.get<any>(`${this.baseUrl}/common/referral`, {
      headers: this.headers
    });
  }

  sendReferralInvites(data: { emails: string[] }) {
    return this.http.post(`${this.baseUrl}/common/referral/invite`, data, {
      headers: this.headers
    });
  }

  getAllNotifications(page = 1, limit = 10) {
    return this.http.get(`${this.baseUrl}/common/notifications?page=${page}&limit=${limit}`, {
      headers: this.headers
    });
  }

  markAllAsReadNotifications() {
    return this.http.post(`${this.baseUrl}/common/notifications/mark-all-read`, {
      headers: this.headers
    });
  }


  // common.service.ts
  getStudentReportAycnSync(format: 'pdf' | 'xlsx') {
    return this.http.get(`${this.baseUrl}/student/report?format=${format}`, {
      headers: this.headers,
      responseType: 'blob' as 'json',
    });
  }

  getStudentReportMentor(format: 'pdf' | 'xlsx') {
    return this.http.get(`${this.baseUrl}/student/report/mentor?format=${format}`, {
      headers: this.headers,
      responseType: 'blob' as 'json',
    });
  }

  getStudentReportCoachin(format: 'pdf' | 'xlsx') {
    return this.http.get(`${this.baseUrl}/student/report/coaching?format=${format}`, {
      headers: this.headers,
      responseType: 'blob' as 'json',
    });
  }


  getUnreadNotificationsCount(): Observable<any> {
    return this.http.get(`${this.baseUrl}/common/notifications/unread`, {
      headers: this.headers,
    });
  }

  getMyNotifications(page = 1, limit = 10): Observable<NotificationsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<NotificationsResponse>(`${this.baseUrl}/common/notifications`, {
      headers: this.headers,
      params,
    });
  }

  markAllNotificationsRead(): Observable<any> {
    return this.http.put(`${this.baseUrl}/common/notifications/mark-all-read`, {}, {
      headers: this.headers,
    });
  }


  // ✅ Get all blogs (requires token)
  getBlogs(page: number = 1, limit: number = 10): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/blogs?page=${page}&limit=${limit}`, {
      headers: this.headers,
    });
  }

  getBlogById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/common/blogs/${id}`, {
      headers: this.headers,
    });
  }


  getPublicYoutubeVideos(
    page = 1,
    limit = 10,
    query?: string,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<YoutubeVideosResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    if (query) params = params.set('query', query);

    return this.http.get<YoutubeVideosResponse>(
      `${this.baseUrl}/common/library-youtube-videos`,
      { headers: this.headers, params }
    );
  }



  getAdminYoutubeVideos(
    page = 1,
    limit = 10,
    query?: string,
    sortBy: 'asc' | 'desc' = 'desc'
  ): Observable<YoutubeVideosResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortBy);

    if (query) params = params.set('query', query);

    return this.http.get<YoutubeVideosResponse>(
      `${this.baseUrl}/admin/library-youtube-videos`,
      { headers: this.headers, params }
    );
  }


  createAdminYoutubeVideo(payload: {
    title: string;
    description?: string;
    imageUrl: string;
  }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/library-youtube-videos`,
      payload,
      { headers: this.headers }
    );
  }


  updateAdminYoutubeVideo(
    id: string,
    payload: { title?: string; description?: string; videoLink?: string }
  ): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/admin/library-youtube-videos/${id}`,
      payload,
      { headers: this.headers }
    );
  }


  deleteAdminYoutubeVideo(id: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/admin/library-youtube-videos/${id}`,
      { headers: this.headers }
    );
  }


  // GET (paginated)
  getLibraryExploreVideos(cardId: string, page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'desc') {
    const params = new HttpParams()
      .set('cardId', cardId)
      .set('page', String(page))
      .set('limit', String(limit))
      .set('sortBy', sortBy);

    return this.http.get<S3VideosResponse>(`${this.baseUrl}/admin/library-explore-videos`, {
      headers: this.headers,
      params
    });
  }

  updateLibraryExploreVideo(id: string, payload: any) {
    return this.http.put(`${this.baseUrl}/admin/library-explore-videos/${id}`, payload, { headers: this.headers });
  }



  createLibraryExploreVideo(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/library-explore-videos`, payload, {
      headers: this.headers,
    });
  }

  deleteLibraryExploreVideo(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/admin/library-explore-videos/${id}`, {
      headers: this.headers,
    });
  }


  getStudentInstructorLibraryExploreVideos(section: 'foundations' | 'advanced', page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'desc') {
    let params = new HttpParams()
      .set('section', section)
      .set('page', String(page))
      .set('limit', String(limit))
      .set('sortBy', sortBy);

    return this.http.get(`${this.baseUrl}/common/library-explore-videos`, {
      headers: this.headers,
      params
    });
  }


  getLibraryExploreVideosPublic(cardId: string, page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'desc') {
    const params = new HttpParams()
      .set('cardId', cardId)
      .set('page', String(page))
      .set('limit', String(limit))
      .set('sortBy', sortBy);

    return this.http.get<any>(`${this.baseUrl}/common/library-explore-videos`, {
      headers: this.headers,
      params
    });
  }

















  // ===========================
  // LEARNING PATH (NEW)
  // ===========================

  // 1) INIT learning path for a package (creates if not exists)
  initPackageLearningPath(packageId: string, payload?: { program?: string }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/package-learning-path/${packageId}/init`,
      payload || {},
      { headers: this.headers }
    );
  }

  // 2) GET full tree (learningPath + modules + categories)
  getPackageLearningPathTree(packageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/${packageId}/tree`,
      { headers: this.headers }
    );
  }

  // 3) LIST modules by package (optional helper)
  listLearningPathModulesByPackage(packageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/${packageId}/modules`,
      { headers: this.headers }
    );
  }

  // ===========================
  // MODULES (NEW)
  // ===========================

  addLearningPathModule(packageId: string, payload: { title: string; subtitle?: string; order?: number }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/package-learning-path/${packageId}/modules`,
      payload,
      { headers: this.headers }
    );
  }

  getLearningPathModule(moduleId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/module/${moduleId}`,
      { headers: this.headers }
    );
  }

  updateLearningPathModule(moduleId: string, payload: { title?: string; subtitle?: string; order?: number }): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/admin/package-learning-path/module/${moduleId}`,
      payload,
      { headers: this.headers }
    );
  }

  deleteLearningPathModule(moduleId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/admin/package-learning-path/module/${moduleId}`,
      { headers: this.headers }
    );
  }

  // ===========================
  // CATEGORIES (NEW) - inside module
  // ===========================

  listLearningPathCategories(moduleId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/module/${moduleId}/categories`,
      { headers: this.headers }
    );
  }

  addLearningPathCategory(moduleId: string, payload: {
    title: string;
    subtitle?: string;
    mini?: string;
    key?: string; // optional slug-like
    order?: number;
  }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/package-learning-path/module/${moduleId}/categories`,
      payload,
      { headers: this.headers }
    );
  }

  getLearningPathCategory(categoryId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/category/${categoryId}`,
      { headers: this.headers }
    );
  }

  updateLearningPathCategory(categoryId: string, payload: {
    title?: string;
    subtitle?: string;
    mini?: string;
    key?: string;
    order?: number;
  }): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/admin/package-learning-path/category/${categoryId}`,
      payload,
      { headers: this.headers }
    );
  }

  deleteLearningPathCategory(categoryId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/admin/package-learning-path/category/${categoryId}`,
      { headers: this.headers }
    );
  }

  // ===========================
  // ITEMS (NEW) - inside category
  // ===========================

  // ✅ PAGINATED LIST (same pagination structure you gave)
  listLearningPathCategoryItems(categoryId: string, params?: {
    page?: number;
    limit?: number;
    sortBy?: 'asc' | 'desc';
    search?: string;
  }): Observable<any> {
    const qp = new URLSearchParams();
    if (params?.page) qp.set('page', String(params.page));
    if (params?.limit) qp.set('limit', String(params.limit));
    if (params?.sortBy) qp.set('sortBy', params.sortBy);
    if (params?.search) qp.set('search', params.search);

    const query = qp.toString() ? `?${qp.toString()}` : '';
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/category/${categoryId}/items${query}`,
      { headers: this.headers }
    );
  }

  addLearningPathItem(categoryId: string, payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/package-learning-path/category/${categoryId}/items`,
      payload,
      { headers: this.headers }
    );
  }

  getLearningPathItem(itemId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/admin/package-learning-path/item/${itemId}`,
      { headers: this.headers }
    );
  }

  updateLearningPathItem(itemId: string, payload: any): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/admin/package-learning-path/item/${itemId}`,
      payload,
      { headers: this.headers }
    );
  }

  deleteLearningPathItem(itemId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/admin/package-learning-path/item/${itemId}`,
      { headers: this.headers }
    );
  }


  bulkAddLearningPathItems(payload: {
    targets: { categoryId: string }[];
    items: any[];
  }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/admin/package-learning-path/items/bulk`,
      payload,
      { headers: this.headers }
    );
  }



  bulkUpdateLearningPathItemsBySharedKey(payload: { sharedKey: string; patch: any }) {
  return this.http.post(
    `${this.baseUrl}/admin/package-learning-path/items/bulk-update`,
    payload,
    { headers: this.headers }
  );
}

bulkDeleteLearningPathItemsBySharedKey(payload: { sharedKey: string }) {
  return this.http.post(
    `${this.baseUrl}/admin/package-learning-path/items/bulk-delete`,
    payload,
    { headers: this.headers }
  );
}






  // ===========================
// INSTRUCTOR GUIDE (ADMIN) - NEW
// ===========================

// 1) INIT instructor guide (singleton root doc)
initInstructorGuide(payload?: { program?: string }): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/instructor-guide/init`,
    payload || {},
    { headers: this.headers }
  );
}

// 2) GET full tree (instructorGuide + modules + categories)
getInstructorGuideTree(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/tree`,
    { headers: this.headers }
  );
}

// 3) LIST modules (optional helper)
listInstructorGuideModules(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/modules`,
    { headers: this.headers }
  );
}

// ===========================
// MODULES (INSTRUCTOR GUIDE)
// ===========================

addInstructorGuideModule(payload: { title: string; subtitle?: string; order?: number }): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/instructor-guide/modules`,
    payload,
    { headers: this.headers }
  );
}

getInstructorGuideModule(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/module/${moduleId}`,
    { headers: this.headers }
  );
}

updateInstructorGuideModule(moduleId: string, payload: { title?: string; subtitle?: string; order?: number }): Observable<any> {
  return this.http.patch(
    `${this.baseUrl}/admin/instructor-guide/module/${moduleId}`,
    payload,
    { headers: this.headers }
  );
}

deleteInstructorGuideModule(moduleId: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/instructor-guide/module/${moduleId}`,
    { headers: this.headers }
  );
}

// ===========================
// CATEGORIES (INSTRUCTOR GUIDE) - inside module
// ===========================

listInstructorGuideCategories(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/module/${moduleId}/categories`,
    { headers: this.headers }
  );
}

addInstructorGuideCategory(moduleId: string, payload: {
  title: string;
  subtitle?: string;
  mini?: string;
  key?: string;   // optional slug-like
  order?: number;
}): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/instructor-guide/module/${moduleId}/categories`,
    payload,
    { headers: this.headers }
  );
}

getInstructorGuideCategory(categoryId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/category/${categoryId}`,
    { headers: this.headers }
  );
}

updateInstructorGuideCategory(categoryId: string, payload: {
  title?: string;
  subtitle?: string;
  mini?: string;
  key?: string;
  order?: number;
}): Observable<any> {
  return this.http.patch(
    `${this.baseUrl}/admin/instructor-guide/category/${categoryId}`,
    payload,
    { headers: this.headers }
  );
}

deleteInstructorGuideCategory(categoryId: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/instructor-guide/category/${categoryId}`,
    { headers: this.headers }
  );
}

// ===========================
// ITEMS (INSTRUCTOR GUIDE) - inside category
// ===========================

// ✅ PAGINATED LIST (same pagination structure)
listInstructorGuideCategoryItems(categoryId: string, params?: {
  page?: number;
  limit?: number;
  sortBy?: 'asc' | 'desc';
  search?: string;
}): Observable<any> {
  const qp = new URLSearchParams();
  if (params?.page) qp.set('page', String(params.page));
  if (params?.limit) qp.set('limit', String(params.limit));
  if (params?.sortBy) qp.set('sortBy', params.sortBy);
  if (params?.search) qp.set('search', params.search);

  const query = qp.toString() ? `?${qp.toString()}` : '';
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/category/${categoryId}/items${query}`,
    { headers: this.headers }
  );
}

addInstructorGuideItem(categoryId: string, payload: any): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/instructor-guide/category/${categoryId}/items`,
    payload,
    { headers: this.headers }
  );
}

getInstructorGuideItem(itemId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/instructor-guide/item/${itemId}`,
    { headers: this.headers }
  );
}

updateInstructorGuideItem(itemId: string, payload: any): Observable<any> {
  return this.http.patch(
    `${this.baseUrl}/admin/instructor-guide/item/${itemId}`,
    payload,
    { headers: this.headers }
  );
}

deleteInstructorGuideItem(itemId: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/instructor-guide/item/${itemId}`,
    { headers: this.headers }
  );
}

// ===========================
// BULK ADD ITEMS (INSTRUCTOR GUIDE)
// ===========================

bulkAddInstructorGuideItems(payload: {
  targets: { categoryId: string }[];
  items: any[];
}): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/instructor-guide/items/bulk`,
    payload,
    { headers: this.headers }
  );
}




  // ✅ Instructor CommonService methods (READ ONLY Learning Path)
// Replace /admin -> /instructor and REMOVE write endpoints (POST/PATCH/DELETE) that don't exist for instructor.

// 1) INIT learning path for a package (Instructor = READ ONLY)
// NOTE: Your instructor backend init does NOT create; it returns existing or 404.
// Keep this method because your UI calls it and then loads tree even on error.
initInstructorPackageLearningPath(packageId: string, payload?: { program?: string }): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/instructor/package-learning-path/${packageId}/init`,
    payload || {},
    { headers: this.headers }
  );
}

// 2) GET full tree (learningPath + modules + categories)
getInstructorPackageLearningPathTree(packageId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/${packageId}/tree`,
    { headers: this.headers }
  );
}

// 3) LIST modules by package (optional helper)
listInstructorLearningPathModulesByPackage(packageId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/${packageId}/modules`,
    { headers: this.headers }
  );
}

// ===========================
// MODULES (READ ONLY)
// ===========================

getInstructorLearningPathModule(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/module/${moduleId}`,
    { headers: this.headers }
  );
}

// ===========================
// CATEGORIES (READ ONLY)
// ===========================

listInstructorLearningPathCategories(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/module/${moduleId}/categories`,
    { headers: this.headers }
  );
}

getInstructorLearningPathCategory(categoryId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/category/${categoryId}`,
    { headers: this.headers }
  );
}

// ===========================
// ITEMS (READ ONLY)
// ===========================

// ✅ PAGINATED LIST (same pagination structure)
listInstructorLearningPathCategoryItems(categoryId: string, params?: {
  page?: number;
  limit?: number;
  sortBy?: 'asc' | 'desc';
  search?: string;
}): Observable<any> {
  const qp = new URLSearchParams();
  if (params?.page) qp.set('page', String(params.page));
  if (params?.limit) qp.set('limit', String(params.limit));
  if (params?.sortBy) qp.set('sortBy', params.sortBy);
  if (params?.search) qp.set('search', params.search);

  const query = qp.toString() ? `?${qp.toString()}` : '';
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/category/${categoryId}/items${query}`,
    { headers: this.headers }
  );
}

getInstructorLearningPathItem(itemId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/package-learning-path/item/${itemId}`,
    { headers: this.headers }
  );
}















// ===================== INSTRUCTOR GUIDE (INSTRUCTOR) =====================

// Tree (modules + categories)
getInstructorGuideTreeForInstructor(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/tree`,
    { headers: this.headers }
  );
}

// Modules
listInstructorGuideModulesForInstructor(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/modules`,
    { headers: this.headers }
  );
}

getInstructorGuideModuleForInstructor(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/module/${moduleId}`,
    { headers: this.headers }
  );
}

// Categories
listInstructorGuideCategoriesForInstructor(moduleId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/module/${moduleId}/categories`,
    { headers: this.headers }
  );
}

getInstructorGuideCategoryForInstructor(categoryId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/category/${categoryId}`,
    { headers: this.headers }
  );
}

// Items
getInstructorGuideItemForInstructor(itemId: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/item/${itemId}`,
    { headers: this.headers }
  );
}

/**
 * Paginated items under a category
 * @example
 * listInstructorGuideCategoryItemsForInstructor(catId, 1, 10, 'asc', 'pdf')
 */
listInstructorGuideCategoryItemsForInstructor(
  categoryId: string,
  page: number = 1,
  limit: number = 10,
  sortBy: "asc" | "desc" = "asc",
  search: string = ""
): Observable<any> {
  const params: any = {
    page: String(page),
    limit: String(limit),
    sortBy: sortBy,
  };

  if (search && search.trim()) params.search = search.trim();

  return this.http.get(
    `${this.baseUrl}/instructor/instructor-guide/category/${categoryId}/items`,
    { headers: this.headers, params }
  );
}


  getStudentLearningPath(packageId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path/${packageId}`,
      { headers: this.headers }
    );
  }

  /**
   * Check if learning path exists for a package (Student)
   * GET /student/learning-path/:packageId/exists
   */
  studentLearningPathExists(packageId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path/${packageId}/exists`,
      { headers: this.headers }
    );
  }



  // ✅ Bulk Import Quizzes (.xlsx)
  importQuizzesFromXlsx(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/quizzes/import-xlsx`,
      formData,
      { headers: this.headers }
    );
  }





  // =========================
  // Admin Community APIs
  // =========================

  adminGetForums(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/community/forums`, {
      headers: this.headers,
    });
  }

  adminUpdateForum(token: string, forumId: string, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/admin/community/forums/${forumId}`, payload, {
      headers: this.headers,
    });
  }

  adminDeleteForum(token: string, forumId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/community/forums/${forumId}`, {
      headers: this.headers,
    });
  }

  // Posts
  adminUpdatePost(token: string, postId: string, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/admin/community/posts/${postId}`, payload, {
      headers: this.headers,
    });
  }

  adminDeletePost(token: string, postId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/community/posts/${postId}`, {
      headers: this.headers,
    });
  }

  // Comments
  adminUpdateComment(token: string, commentId: string, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/admin/community/comments/${commentId}`, payload, {
      headers: this.headers,
    });
  }

  adminDeleteComment(token: string, commentId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/community/comments/${commentId}`, {
      headers: this.headers,
    });
  }

  // Replies
  adminUpdateReply(token: string, replyId: string, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/admin/community/replies/${replyId}`, payload, {
      headers: this.headers,
    });
  }

  adminDeleteReply(token: string, replyId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/community/replies/${replyId}`, {
      headers: this.headers,
    });
  }


  getVideoTranscriptionByResourceId(videoId: string) {
    return this.http.get(`${this.baseUrl}/common/video-transcription/${videoId}`, {
      headers: this.headers
    });
  }






  // ===========================
  // STUDENT - PACKAGE LEARNING PATH (V3)
  // ===========================

  studentLearningPathExistsV3(packageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/${packageId}/exists`,
      { headers: this.headers }
    );
  }

  studentGetLearningPathTreeV3(packageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/${packageId}/tree`,
      { headers: this.headers }
    );
  }

  studentListLearningPathModulesV3(packageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/${packageId}/modules`,
      { headers: this.headers }
    );
  }

  studentListLearningPathCategoriesV3(moduleId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/module/${moduleId}/categories`,
      { headers: this.headers }
    );
  }

  studentGetLearningPathCategoryV3(categoryId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/category/${categoryId}`,
      { headers: this.headers }
    );
  }

  studentListLearningPathCategoryItemsV3(categoryId: string, params?: {
    page?: number;
    limit?: number;
    sortBy?: 'asc' | 'desc';
    search?: string;
  }): Observable<any> {
    const qp = new URLSearchParams();
    if (params?.page) qp.set('page', String(params.page));
    if (params?.limit) qp.set('limit', String(params.limit));
    if (params?.sortBy) qp.set('sortBy', params.sortBy);
    if (params?.search) qp.set('search', params.search);

    const query = qp.toString() ? `?${qp.toString()}` : '';
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/category/${categoryId}/items${query}`,
      { headers: this.headers }
    );
  }

  studentGetLearningPathItemV3(itemId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/student/package-learning-path/item/${itemId}`,
      { headers: this.headers }
    );
  }


  //Get online users
  getOnlineUsers(page: Number, limit: Number, search = '') {
    // if (search && search.trim()) params.search = search.trim();

    return this.http.get<any>(`${this.baseUrl}/student/online-users?page=${page}&limit=${limit}`, { headers: this.headers });
  }



  // =========================
  // ADMIN - RESEARCH LIBRARY (Cards + Items)
  // =========================

  // ✅ Research Library - Cards
  adminCreateResearchLibraryCard(body: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/research-library/cards`,
      body,
      { headers: this.headers }
    );
  }

  adminGetCardItemTags(cardId: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/research/cards/${cardId}/items/tags`, {headers: this.headers});
}

  adminListResearchLibraryCards(params: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;

      const s = String(v).trim();
      if (!s) return;

      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;

      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/admin/research-library/cards`,
      { headers: this.headers, params: clean }
    );
  }


  adminGetResearchLibraryCardById(cardId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/research-library/cards/${cardId}`, {
      headers: this.headers,
    });
  }

  adminUpdateResearchLibraryCard(cardId: string, body: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/research-library/cards/${cardId}`,
      body,
      { headers: this.headers }
    );
  }

  adminDeleteResearchLibraryCard(cardId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/admin/research-library/cards/${cardId}`,
      { headers: this.headers }
    );
  }


  // ---------- Items ----------
  // ✅ Research Library - Items
  adminCreateResearchLibraryItem(cardId: string, body: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/research-library/cards/${cardId}/items`,
      body,
      { headers: this.headers }
    );
  }


  adminListResearchLibraryItems(cardId: string, params: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;

      const s = String(v).trim();
      if (!s) return;

      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;

      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/admin/research-library/cards/${cardId}/items`,
      { headers: this.headers, params: clean }
    );
  }


  adminGetResearchLibraryItemById(itemId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/research-library/items/${itemId}`, {
      headers: this.headers,
    });
  }

  adminUpdateResearchLibraryItem(itemId: string, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/admin/research-library/items/${itemId}`, payload, {
      headers: this.headers,
    });
  }

  adminDeleteResearchLibraryItem(itemId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/admin/research-library/items/${itemId}`, {
      headers: this.headers,
    });
  }




  // ✅ Research Library - Cards (USER)
  userListResearchLibraryCards(params: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;

      const s = String(v).trim();
      if (!s) return;

      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;

      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/common/research-library/cards`,
      { headers: this.headers, params: clean }
    );
  }


  userGetResearchLibraryCardById(cardId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/research-library/cards/${cardId}`,
      { headers: this.headers }
    );
  }


  userListResearchLibraryItems(cardId: string, params: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;

      const s = String(v).trim();
      if (!s) return;

      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;

      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/common/research-library/cards/${cardId}/items`,
      { headers: this.headers, params: clean }
    );
  }


  userGetResearchLibraryItemById(itemId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/common/research-library/items/${itemId}`,
      { headers: this.headers }
    );
  }


  // ✅ UPDATED CommonService methods for NEW Admin Learning Path Instructions routes
  // (Steps + Per-Step Sections + Global Videos + Videos Block Config + Combined Screen Data)

  private cleanParams(params?: any): any {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;
      clean[k] = v;
    });
    return clean;
  }

  /** ===================== Screen Data (cards view expects this) ===================== */
  // GET /admin/learning-path-instructions
  // Suggested response: { steps, videos, videosBlockConfig } (and optionally counts)
  adminGetLearningPathInstructionsScreenData(params?: any): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions`,
      { headers: this.headers, params: this.cleanParams(params) }
    );
  }

  /** ===================== Videos Block Config (header/subtitle + enable) ===================== */
  // GET /admin/learning-path-instructions/config/videos-block
  adminGetLearningPathVideosBlockConfig(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/config/videos-block`,
      { headers: this.headers }
    );
  }

  // PATCH /admin/learning-path-instructions/config/videos-block
  // body example: { title: 'Learning Path — Videos', subtitle: '...', enabled: true }
  adminUpdateLearningPathVideosBlockConfig(body: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/config/videos-block`,
      body,
      { headers: this.headers }
    );
  }

  /** ===================== Steps ===================== */
  // POST /admin/learning-path-instructions/steps
  // body example: { title: 'STEP ONE', subtitle: 'Welcome & Setup', sortOrder?: number }
  adminCreateLearningPathInstructionStep(body: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps`,
      body,
      { headers: this.headers }
    );
  }

  // GET /admin/learning-path-instructions/steps?search=&state=&page=&limit=
  // (state optional if you implement, else ignore)
  adminListLearningPathInstructionSteps(params?: any): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps`,
      { headers: this.headers, params: this.cleanParams(params) }
    );
  }

  // GET /admin/learning-path-instructions/steps/:stepId
  adminGetLearningPathInstructionStepById(stepId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}`,
      { headers: this.headers }
    );
  }

  // PATCH /admin/learning-path-instructions/steps/:stepId
  adminUpdateLearningPathInstructionStep(stepId: string, body: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}`,
      body,
      { headers: this.headers }
    );
  }

  // DELETE /admin/learning-path-instructions/steps/:stepId
  adminDeleteLearningPathInstructionStep(stepId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}`,
      { headers: this.headers }
    );
  }

  // ✅ GET /admin/learning-path-instructions/steps/:stepId/detail
  // returns: { step, sections } (and optionally videos if you want)
  adminGetLearningPathInstructionStepDetail(stepId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}/detail`,
      { headers: this.headers }
    );
  }

  /** ===================== Sections (Per Step) ===================== */
  // POST /admin/learning-path-instructions/steps/:stepId/sections
  // body example: { title, subtitle?, body?, bullets? or bulletsText?, state?, sortOrder? }
  adminCreateLearningPathSection(stepId: string, body: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}/sections`,
      body,
      { headers: this.headers }
    );
  }

  // GET /admin/learning-path-instructions/steps/:stepId/sections?search=&state=&page=&limit=
  adminListLearningPathSectionsByStep(stepId: string, params?: any): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/steps/${stepId}/sections`,
      { headers: this.headers, params: this.cleanParams(params) }
    );
  }

  // PATCH /admin/learning-path-instructions/sections/:sectionId
  adminUpdateLearningPathSection(sectionId: string, body: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/sections/${sectionId}`,
      body,
      { headers: this.headers }
    );
  }

  // PATCH /admin/learning-path-instructions/sections/:sectionId/toggle
  adminToggleLearningPathSection(sectionId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/sections/${sectionId}/toggle`,
      {},
      { headers: this.headers }
    );
  }

  // DELETE /admin/learning-path-instructions/sections/:sectionId
  adminDeleteLearningPathSection(sectionId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/admin/learning-path-instructions/sections/${sectionId}`,
      { headers: this.headers }
    );
  }

  /** ===================== Videos (Global Rail) ===================== */
  // POST /admin/learning-path-instructions/videos
  adminCreateLearningPathVideo(body: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos`,
      body,
      { headers: this.headers }
    );
  }

  // GET /admin/learning-path-instructions/videos?search=&state=&page=&limit=
  adminListLearningPathVideos(params?: any): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos`,
      { headers: this.headers, params: this.cleanParams(params) }
    );
  }

  // GET /admin/learning-path-instructions/videos/:videoId
  adminGetLearningPathVideoById(videoId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos/${videoId}`,
      { headers: this.headers }
    );
  }

  // PATCH /admin/learning-path-instructions/videos/:videoId
  adminUpdateLearningPathVideo(videoId: string, body: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos/${videoId}`,
      body,
      { headers: this.headers }
    );
  }

  // PATCH /admin/learning-path-instructions/videos/:videoId/toggle
  adminToggleLearningPathVideo(videoId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos/${videoId}/toggle`,
      {},
      { headers: this.headers }
    );
  }

  // DELETE /admin/learning-path-instructions/videos/:videoId
  adminDeleteLearningPathVideo(videoId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/admin/learning-path-instructions/videos/${videoId}`,
      { headers: this.headers }
    );
  }



  // ==============================
  // STUDENT - Learning Path Instructions
  // ==============================
  // Screen data (steps grid + videos block + videos rail)
  studentGetLearningPathInstructionsScreen(params?: { search?: string; state?: 'published' | 'draft' }) {
    let httpParams = new HttpParams();
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.state) httpParams = httpParams.set('state', params.state);

    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path-instructions/screen`,
      { headers: this.headers, params: httpParams }
    );
  }

  // Step detail (when student opens a step)
  studentGetLearningPathInstructionStepDetail(
    stepId: string,
    params?: { search?: string; state?: 'published' | 'draft' }
  ) {
    let httpParams = new HttpParams();
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.state) httpParams = httpParams.set('state', params.state);

    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path-instructions/steps/${stepId}`,
      { headers: this.headers, params: httpParams }
    );
  }

  // Optional single fetches (same paths as before)
  studentGetLearningPathInstructionSection(sectionId: string) {
    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path-instructions/sections/${sectionId}`,
      { headers: this.headers }
    );
  }

  studentGetLearningPathInstructionVideo(videoId: string) {
    return this.http.get<any>(
      `${this.baseUrl}/student/learning-path-instructions/videos/${videoId}`,
      { headers: this.headers }
    );
  }


  // ==============================
  // Instructor - Learning Path Instructions
  // ==============================
  // Screen data (steps grid + videos block + videos rail)
  instructorGetLearningPathInstructionsScreen(params?: { search?: string; state?: 'published' | 'draft' }) {
    let httpParams = new HttpParams();
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.state) httpParams = httpParams.set('state', params.state);

    return this.http.get<any>(
      `${this.baseUrl}/instructor/learning-path-instructions/screen`,
      { headers: this.headers, params: httpParams }
    );
  }

  // Step detail (when student opens a step)
  instructorGetLearningPathInstructionStepDetail(
    stepId: string,
    params?: { search?: string; state?: 'published' | 'draft' }
  ) {
    let httpParams = new HttpParams();
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.state) httpParams = httpParams.set('state', params.state);

    return this.http.get<any>(
      `${this.baseUrl}/instructor/learning-path-instructions/steps/${stepId}`,
      { headers: this.headers, params: httpParams }
    );
  }

  // Optional single fetches (same paths as before)
  instructorGetLearningPathInstructionSection(sectionId: string) {
    return this.http.get<any>(
      `${this.baseUrl}/instructor/learning-path-instructions/sections/${sectionId}`,
      { headers: this.headers }
    );
  }

  instructorGetLearningPathInstructionVideo(videoId: string) {
    return this.http.get<any>(
      `${this.baseUrl}/instructor/learning-path-instructions/videos/${videoId}`,
      { headers: this.headers }
    );
  }

  getSupportContact(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/support-contact`, {
      headers: this.headers,
    });
  }

  updateSupportContact(payload: { email: string; phone: string }): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/admin/support-contact`, payload, {
      headers: this.headers,
    });
  }


  getStudentSupportContact(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/student/support/contact`, {
      headers: this.headers,
    });
  }



  // ============================
// Learning Path Header (Dynamic)
// ============================

// ADMIN: init (creates default once)
initLearningPathHeader(): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/learning-path/header/init`,
    {},
    { headers: this.headers }
  );
}

// ADMIN: get
getLearningPathHeader(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/learning-path/header`,
    { headers: this.headers }
  );
}

// ADMIN: update
updateLearningPathHeader(payload: {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  noteText?: string;
}): Observable<any> {
  return this.http.patch(
    `${this.baseUrl}/admin/learning-path/header`,
    payload,
    { headers: this.headers }
  );
}



// ADMIN: get
getCommonLearningPathHeader(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/common/learning-path/header`,
    { headers: this.headers }
  );
}


  // ------- Admin Student Progress -------

  adminListStudentsProgress(params?: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;
      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/admin/students/progress`,
      { headers: this.headers, params: clean }
    );
  }

  adminGetStudentProgressById(studentUserId: string, params?: any): Observable<any> {
    const clean: any = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null') return;
      clean[k] = v;
    });

    return this.http.get<any>(
      `${this.baseUrl}/admin/students/${studentUserId}/progress`,
      { headers: this.headers, params: clean }
    );
  }


  // ADMIN: Update student/instructor email or password
  adminUpdateUserCredentials(
    userId: string,
    payload: { email?: string; password?: string; forceLogout?: boolean }
  ) {
    return this.http.patch<any>(
      `${this.baseUrl}/admin/users/${userId}/credentials`,
      payload,
      { headers: this.headers } // use your existing token header helper
    );
  }


  getEmailBrand() {
  return this.http.get(`${this.baseUrl}/admin/email-brand`, { headers: this.headers });
}

updateEmailBrand(payload: any) {
  return this.http.put(`${this.baseUrl}/admin/email-brand`, payload, { headers: this.headers });
}





// 🔹 Elective Webinars APIs
createElectiveWebinar(payload: any): Observable<any> {
  return this.http.post(`${this.baseUrl}/admin/elective-webinar`, payload, {
    headers: this.headers,
  });
}

updateElectiveWebinar(id: string, payload: any): Observable<any> {
  return this.http.put(`${this.baseUrl}/admin/elective-webinar/${id}`, payload, {
    headers: this.headers,
  });
}

getElectiveWebinarById(id: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/elective-webinar/${id}`, {
    headers: this.headers,
  });
}

deleteElectiveWebinar(id: string): Observable<any> {
  return this.http.delete(`${this.baseUrl}/admin/elective-webinar/${id}`, {
    headers: this.headers,
  });
}

getElectiveWebinars(page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'desc'): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/elective-webinar?page=${page}&limit=${limit}&sortBy=${sortBy}`,
    { headers: this.headers }
  );
}



// In your common.service.ts file, add these methods:

// 🔹 Community Sessions APIs
createCommunitySession(payload: any): Observable<any> {
  return this.http.post(`${this.baseUrl}/admin/community-session`, payload, {
    headers: this.headers,
  });
}

updateCommunitySession(id: string, payload: any): Observable<any> {
  return this.http.put(`${this.baseUrl}/admin/community-session/${id}`, payload, {
    headers: this.headers,
  });
}

getCommunitySessionById(id: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/admin/community-session/${id}`, {
    headers: this.headers,
  });
}

deleteCommunitySession(id: string): Observable<any> {
  return this.http.delete(`${this.baseUrl}/admin/community-session/${id}`, {
    headers: this.headers,
  });
}

getCommunitySessions(page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'desc'): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/community-session?page=${page}&limit=${limit}&sortBy=${sortBy}`,
    { headers: this.headers }
  );
}

getCommunitySessionsByMonth(month: string, year: string): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/community-session/by-month?month=${month}&year=${year}`,
    { headers: this.headers }
  );
}
// (Optional if you add backend route)
// completeElectiveWebinar(webinarId: string): Observable<any> {
//   return this.http.post(`${this.baseUrl}/admin/elective-webinar/complete`, { webinarId }, {
//     headers: this.headers,
//   });
// }








// ==================== PUBLIC COMMUNITY SESSION METHODS (No Auth Required) ====================

/**
 * Get all upcoming community sessions with available spots
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 10)
 * @param sortBy - Sort order 'asc' or 'desc' (default: 'asc')
 * @param month - Filter by month name (optional)
 */
getUpcomingCommunitySessions(page = 1, limit = 10, sortBy: 'asc' | 'desc' = 'asc', month = ''): Observable<any> {
  let url = `${this.baseUrl}/common/community-session/upcoming?page=${page}&limit=${limit}&sortBy=${sortBy}`;
  if (month) {
    url += `&month=${encodeURIComponent(month)}`;
  }
  return this.http.get(url, {
    headers: this.headers,
  });
}

/**
 * Get featured session for current month
 */
getFeaturedCommunitySession(): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/featured`, {
    headers: this.headers,
  });
}

/**
 * Get all unique causes/charities for filtering
 */
getAllCommunityCauses(): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/causes`);
}

// ==================== PROTECTED COMMUNITY SESSION METHODS (Auth Required) ====================

/**
 * Create Stripe checkout session for community session registration
 * @param payload - Registration data
 */
createCommunitySessionCheckout(payload: {
  communitySessionId: string;
  sessionId: string;
  firstName: string;
  lastName: string;
  email: string;
  ref?: string; // Optional referral code
}): Observable<any> {
  return this.http.post(`${this.baseUrl}/common/community-session/create-checkout`, payload, {
    headers: this.headers,
  });
}

/**
 * Verify Stripe payment and complete registration
 * @param sessionId - Stripe checkout session ID
 */
verifyCommunitySessionCheckout(sessionId: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/verify-checkout?session_id=${sessionId}`, {
    headers: this.headers,
  });
}

/**
 * Get user's registered community sessions
 * @param status - Filter by status (optional: 'registered', 'cancelled', 'attended', 'no_show')
 */
getMyCommunitySessions(status: 'registered' | 'cancelled' | 'attended' | 'no_show' = 'registered'): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/my-registrations?status=${status}`, {
    headers: this.headers,
  });
}

/**
 * Cancel a community session registration
 * @param registrationId - Registration ID to cancel
 */
cancelCommunitySessionRegistration(registrationId: string): Observable<any> {
  return this.http.post(`${this.baseUrl}/common/community-session/${registrationId}/cancel`, {}, {
    headers: this.headers,
  });
}

/**
 * Get upcoming registrations (sessions that haven't started yet)
 */
getMyUpcomingCommunitySessions(): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/my-registrations/upcoming`, {
    headers: this.headers,
  });
}

/**
 * Get past registrations (completed sessions)
 */
getMyPastCommunitySessions(): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/my-registrations/past`, {
    headers: this.headers,
  });
}

/**
 * Get registration details by ID
 * @param registrationId - Registration ID
 */
getCommunitySessionRegistrationById(registrationId: string): Observable<any> {
  return this.http.get(`${this.baseUrl}/common/community-session/registrations/${registrationId}`, {
    headers: this.headers,
  });
}








// ✅ PUBLIC: website live sessions page texts
getLiveSessionsPageContentPublic() {
  return this.http.get(`${this.baseUrl}/common/live-sessions-page-content`, {
    headers: this.headers,
  });
}

// ✅ ADMIN: fetch current editable texts
getLiveSessionsPageContentAdmin() {
  return this.http.get(`${this.baseUrl}/admin/live-sessions-page-content`, {
    headers: this.headers,
  });
}

// ✅ ADMIN: update texts (partial update allowed)
updateLiveSessionsPageContent(payload: any) {
  return this.http.put(`${this.baseUrl}/admin/live-sessions-page-content`, payload, {
    headers: this.headers,
  });
}







// ✅ ADMIN: Get all free sessions
getAdminFreeSessions(page: number, limit: number, sortBy: string = 'desc') {
  return this.http.get(
    `${this.baseUrl}/admin/free-sessions?page=${page}&limit=${limit}&sortBy=${sortBy}`,
    { headers: this.headers }
  );
}

// ✅ ADMIN: Get participants of a specific session
getAdminSessionParticipants(
  eventId: string,
  sessionId: string,
  page: number,
  limit: number,
  search: string = '',
  sortBy: string = 'desc'
) {
  return this.http.get(
    `${this.baseUrl}/admin/free-sessions/${eventId}/sessions/${sessionId}/participants?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy}`,
    { headers: this.headers }
  );
}

getAllFreeSessionParticipants(
  page: number,
  limit: number,
  search: string = '',
  sortBy: string = 'desc'
) {
  return this.http.get(`${this.baseUrl}/admin/free-session-participants`, {
    params: { page, limit, search, sortBy }, // ✅
    headers: this.headers
  });
}

exportFreeSessionParticipants() {
  return this.http.get(
    `${this.baseUrl}/admin/free-session-participants/export`,
    { headers: this.headers, responseType: 'blob' } // ✅ IMPORTANT
  );
}

exportFreeSessionParticipantsExcel() {
  return this.http.get(
    `${this.baseUrl}/admin/free-session-participants/export/excel`,
    { headers: this.headers, responseType: 'blob' }
  );
}

exportFreeSessionParticipantsPDF() {
  return this.http.get(
    `${this.baseUrl}/admin/free-session-participants/export/pdf`,
    { headers: this.headers, responseType: 'blob' }
  );
}


getAdminCommunitySessions(page = 1, limit = 100): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/community-session-participants/sessions?page=${page}&limit=${limit}`,
    { headers: this.headers }
  );
}
 
getCommunitySessionParticipants(
  communitySessionId: string,
  sessionId: string,
  page = 1,
  limit = 10,
  search = '',
  sortBy = 'desc'
): Observable<any> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search,
    sortBy,
  });
  return this.http.get(
    `${this.baseUrl}/admin/community-session-participants/${communitySessionId}/sessions/${sessionId}/participants?${params}`,
    { headers: this.headers }
  );
}
 
getAllCommunitySessionParticipants(
  page = 1,
  limit = 10,
  search = '',
  sortBy = 'desc'
): Observable<any> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search,
    sortBy,
  });
  return this.http.get(
    `${this.baseUrl}/admin/community-session-participants?${params}`,
    { headers: this.headers }
  );
}
 
exportCommunitySessionParticipantsExcel(
  communitySessionId?: string,
  sessionId?: string
): Observable<any> {
  let url = `${this.baseUrl}/admin/community-session-participants/export/excel`;
  const params: string[] = [];
  if (communitySessionId) params.push(`communitySessionId=${communitySessionId}`);
  if (sessionId) params.push(`sessionId=${sessionId}`);
  if (params.length) url += '?' + params.join('&');
 
  return this.http.get(url, {
    headers: this.headers,
    responseType: 'blob',
  });
}
 
exportCommunitySessionParticipantsPDF(
  communitySessionId?: string,
  sessionId?: string
): Observable<any> {
  let url = `${this.baseUrl}/admin/community-session-participants/export/pdf`;
  const params: string[] = [];
  if (communitySessionId) params.push(`communitySessionId=${communitySessionId}`);
  if (sessionId) params.push(`sessionId=${sessionId}`);
  if (params.length) url += '?' + params.join('&');
 
  return this.http.get(url, {
    headers: this.headers,
    responseType: 'blob',
  });
}





// ── Free session: delete single ────────────────────────────────────────────
deleteFreeSessionParticipant(registrationId: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/free-session-participants/${registrationId}`,
    { headers: this.headers }
  );
}

// ── Free session: bulk delete ──────────────────────────────────────────────
bulkDeleteFreeSessionParticipants(ids: string[]): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/free-session-participants/bulk`,
    { headers: this.headers, body: { ids } }
  );
}

// ── Community session: delete single ──────────────────────────────────────
deleteCommunitySessionParticipant(registrationId: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/community-session-participants/${registrationId}`,
    { headers: this.headers }
  );
}

// ── Community session: bulk delete ────────────────────────────────────────
bulkDeleteCommunitySessionParticipants(ids: string[]): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/community-session-participants/bulk`,
    { headers: this.headers, body: { ids } }
  );
}




// GET my profile
getAdminMyProfile(): Observable<any> {
  return this.http.get(
    `${this.baseUrl}/admin/profile/me`,
    { headers: this.headers }
  );
}
 
// PUT update my profile
updateAdminProfile(data: { firstName?: string; lastName?: string; phoneNumber?: string }): Observable<any> {
  return this.http.put(
    `${this.baseUrl}/admin/profile/update`,
    data,
    { headers: this.headers }
  );
}
 
// PUT change my password
changeAdminPassword(data: {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}): Observable<any> {
  return this.http.put(
    `${this.baseUrl}/admin/profile/change-password`,
    data,
    { headers: this.headers }
  );
}
 
// POST create new admin
createAdminAccount(data: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
}): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/admin/profile/create-admin`,
    data,
    { headers: this.headers }
  );
}
 
// GET all admins (paginated)
getAllAdmins(page = 1, limit = 10, search = '', sortBy = 'desc'): Observable<any> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search,
    sortBy,
  });
  return this.http.get(
    `${this.baseUrl}/admin/profile/admins?${params}`,
    { headers: this.headers }
  );
}
 
// PUT update another admin
updateAdminById(id: string, data: {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  status?: string;
}): Observable<any> {
  return this.http.put(
    `${this.baseUrl}/admin/profile/admins/${id}`,
    data,
    { headers: this.headers }
  );
}
 
// DELETE deactivate admin
deleteAdminById(id: string): Observable<any> {
  return this.http.delete(
    `${this.baseUrl}/admin/profile/admins/${id}`,
    { headers: this.headers }
  );
}
 
 firstTimeChangePassword(payload: { newPassword: string; confirmPassword: string }, token: string): Observable<any> {
  return this.http.post(`${this.baseUrl}/common/first-time-change-password`, payload, { headers: this.headers });
}
}
